/**
 * Typed cloud JSON client. The ONLY network surface for the clients:
 * auth/activation/entitlements/approvals/sync/AI-relay/telemetry/dashboard.
 *
 * There is NO server render endpoint (Constitution III). All contracts:
 *   006 auth-api.md · 009 ops-api.md · 007 relay-api.md · 003 catalog-api.md
 *   002 editor-approval.md · 010 telemetry-and-dashboard.md
 */
import type {
  AccessTokenClaims,
  ActivationStatus,
  AiSettings,
  AiSettingsInput,
  AiCallLogEntry,
  AiUsage,
  HiggsfieldUsage,
  HiggsfieldUsageQuery,
  HiggsfieldSubscriptionLogin,
  HiggsfieldSubscriptionStatus,
  AuthTokens,
  CreatedTemplate,
  AssignLicenseInput,
  BrandProfile,
  BrandStatus,
  CreateBrandInput,
  Appearance,
  CreateCustomerInput,
  CustomizationCapabilities,
  DashboardSummary,
  EntitlementsByTier,
  FtpConfig,
  UpdateAppearanceInput,
  ModelRequest,
  ReferenceUploadTarget,
  TemplateOptions,
  ResolvedOptions,
  OptionGroup,
  OptionChoice,
  OptionAssignment,
  UpdateSettingsInput,
  ResolvedWorkspaceSettings,
  UpdateBrandInput,
  CreateLicenseInput,
  CreatedLicense,
  CreateTemplateInput,
  CustomerDetail,
  CustomerRecord,
  DeviceInfo,
  Entitlements,
  BoundDevice,
  LicenseDetail,
  LicenseSummary,
  CaseJobStage,
  CaseJobView,
  CaseRuleKey,
  GenerateResult,
  GenerationHandoffStep,
  GenerationJobExecutor,
  GenerationJobView,
  GenerationJobsSummary,
  GenerationRunsPage,
  GenerationPrintArea,
  GenerationOutput,
  GenerationDetailsEdit,
  HostingSettings,
  HostingSettingsInput,
  OutputDescriptor,
  ProductTemplate,
  TemplateDisplay,
  PhoneModelOption,
  CaseStyleOption,
  ProductTypeAdmin,
  ImageAsset,
  ImageAssetKind,
  Role,
  SignedUpload,
  TemplateMatch,
  TemplateStatus,
  TemplateVersion,
  AddCommentInput,
  CommentType,
  ConnectorTestResult,
  CreateConnectorInput,
  ReviewComment,
  SignedInDevice,
  StorageConnector,
  TemplateOwnership,
  TemplateOwnershipInput,
  UpdateConnectorInput,
  UpdateCustomerInput,
  UploadLogEntry,
  UpdateLicenseInput,
  ClientLogRow,
} from "./types.js";
// F-4: `normalizeMasksForWire` is the ONE legacy-mask normaliser, shared with the RENDER
// read seam (`@genz/engine-bind`'s `adapter.ts`). It used to be a private copy here, which is
// why the write path normalised and the render path did not.
import { normalizeMasksForWire } from "@genz/engine-bind";
import type { WireVersionPayload } from "@genz/engine-bind";
import type {
  ExportRecord,
  HeartbeatPayload,
  JobRecord,
  JobStatus,
  LogEntry,
  TelemetryEvent,
  TraceEvent,
  TraceLevel,
} from "./telemetry/types.js";
import { parseServerTs } from "./serverTime.js";

export interface JsonClientOptions {
  baseUrl: string; // e.g. http://localhost:8000
  /** Called when refresh fails — UI should route to login. */
  onAuthExpired?: () => void;
  /**
   * Called whenever the tokens change (login, transparent refresh, logout). The
   * app persists these so a restart stays logged in and the ROTATED refresh token
   * isn't lost. Fired with `undefined` is left to onAuthExpired/clear.
   */
  onTokensChanged?: (tokens: AuthTokens | undefined) => void;
  /**
   * The tokens as last PERSISTED, read fresh — another tab of the same browser may have rotated
   * them. Read after taking the cross-tab refresh lock, so this tab adopts the rotation instead
   * of presenting the refresh token it holds, which the server would count as a reuse.
   */
  readStoredTokens?: () => AuthTokens | undefined;
  fetchImpl?: typeof fetch;
  /**
   * Per-request hard timeout (ms). A request that never responds is aborted and
   * rejected so the UI never hangs forever (the old "stuck on Generating…" symptom).
   * Generous default to accommodate slow inline AI generation; defaults to 180s.
   */
  requestTimeoutMs?: number;
  /**
   * Backoff before each retry of an idempotent read, in ms (default `[250, 1000]`, i.e.
   * at most two retries). Each delay gets ±20 % jitter. Exposed so tests can pass
   * `[0, 0]` instead of making the suite sleep 1.25 s per retry case.
   */
  retryDelaysMs?: number[];
  /**
   * Called after every request completes or rejects. Receives the path-only URL,
   * HTTP method, status (when the server responded), duration, and ok/error flags.
   * Used by the ClientLogger to capture a structured network event.
   * Wrapped in try/catch — a throwing hook never breaks the request path.
   */
  /**
   * WS-Q Q-8 / MIN-17: this machine's clock minus the server's, in ms, measured ONCE per
   * session from the first authenticated response.
   *
   * It explains the one field report that is neither IMP-13 nor IMP-15: "it logs me out
   * constantly, but only on this machine". The client decides when to refresh PROACTIVELY by
   * comparing the access token's `exp` against the local clock, so a fast clock refreshes on
   * every request — extra rotations, and every rotation is one more chance for the reuse
   * detector to fire — while a slow one never refreshes proactively and takes the 401 path
   * each time. Neither is visible from the server, and neither looks like a clock problem
   * from the desk. One subtraction, one log line.
   */
  onClockSkew?: (deltaMs: number) => void;
  onRequestObserved?: (r: {
    method: string;
    urlPath: string;
    status?: number;
    durationMs: number;
    ok: boolean;
    error?: string;
    /** The X-Request-Id this client attached — the same id the cloud echoes into any
     *  error it records, so one search finds both sides of a failure. */
    requestId?: string;
    /** The server's parsed error body on a non-ok response — the actual REASON, which used
     *  to be read after the observation hook had already fired and then thrown away. */
    errorBody?: unknown;
  }) => void;
}

/**
 * Decode the access-token payload (the middle JWT segment) WITHOUT verifying the
 * signature — the server is the only authority and re-checks every request. This
 * is purely so the UI can show a REAL tenant/customer/role identity instead of the
 * "self" placeholders. Returns undefined for any malformed/placeholder token.
 */
export function decodeAccessToken(accessToken: string): AccessTokenClaims | undefined {
  const segments = accessToken.split(".");
  if (segments.length !== 3) return undefined;
  try {
    const payload = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const json =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("binary");
    const claims = JSON.parse(json) as Record<string, unknown>;
    const customer = (claims.customer ?? claims.sub) as string | undefined;
    const role = claims.role as Role | undefined;
    if (!customer || !role) return undefined;
    return {
      customer,
      tenant: (claims.tenant as string | null | undefined) ?? null,
      role,
      sub: claims.sub as string | undefined,
      exp: claims.exp as number | undefined,
      chain: claims.chain as string | undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * True when `accessToken`'s `exp` is in the past (with a small clock-skew margin), so the
 * caller should refresh BEFORE sending. Returns false when the expiry can't be determined
 * (malformed/opaque token) — we then fall back to the reactive 401-refresh path rather than
 * refreshing speculatively. Prevents the cold-start burst of guaranteed-401 requests (and
 * their console errors) when a persisted session's access token has already expired.
 */
export function accessTokenExpired(accessToken: string, skewMs = 10_000): boolean {
  const exp = decodeAccessToken(accessToken)?.exp;
  if (typeof exp !== "number") return false;
  return exp * 1000 <= Date.now() + skewMs;
}

/**
 * What `/health` answers. `db` is `"ok" | "error" | "unchecked"` — and `"unchecked"` is the
 * shallow probe saying it never looked, which is NOT a failure. `seed` is present only on a
 * deep probe: `"pending"` while the background migrate+seed is still running (the normal early
 * state of every Postgres boot), `"ok"` once it finished, `"incomplete"` if it failed.
 */
export interface HealthReport {
  status: string;
  db: string;
  migrations: string;
  seed?: string;
}

/** Whether an error body is a health report rather than, say, a gateway's own 503 page. */
function isHealthReport(body: unknown): body is HealthReport {
  if (typeof body !== "object" || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.status === "string" && typeof b.db === "string";
}

export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/** A request cancelled because its session ended: the caller treats it as cancelled, not failed. */
export class SignedOutError extends Error {
  constructor() {
    super("Request cancelled: signed out");
    this.name = "SignedOutError";
  }
}

export function isSignedOut(e: unknown): e is SignedOutError {
  return e instanceof SignedOutError;
}

interface LoginResponse extends AuthTokens {
  role: Role;
}

/** Raw cloud `VersionOut` (catalog/schemas.py) — snake_case over the wire. */
interface WireVersionOut {
  id: string;
  template_id: string;
  version: number;
  status: TemplateStatus;
  locked: boolean;
  version_token: string;
  created_by: string | null;
  submitted_by: string | null;
  approved_by: string | null;
  payload: WireVersionPayload;
  active_from?: string | null;
  baked_assets?: Record<string, unknown> | null;
  /** WS-P. Optional: a pre-WS-P server omits it, and `undefined` must stay distinguishable
   *  from `false` — see the note on `TemplateVersion.hasBakedAssets`. */
  has_baked_assets?: boolean;
}

/** Map a cloud `VersionOut` to the client `TemplateVersion` view-model. */
function wireVersionToView(v: WireVersionOut): TemplateVersion {
  return {
    versionId: v.id,
    versionNumber: v.version,
    status: v.status,
    versionToken: v.version_token,
    submittedBy: v.submitted_by ?? undefined,
    approvedBy: v.approved_by ?? undefined,
    activeFrom: v.active_from ?? undefined,
    payload: v.payload,
    // Spec 019: carry the baked-asset refs so the client can hydrate them on sync.
    bakedAssets: v.baked_assets ?? null,
    // WS-P: the cloud's own answer to "did this version publish a mock-up". `??` would
    // collapse the server's silence into `false` and slander a pre-WS-P cloud's templates as
    // unpublished, so an absent field stays absent.
    hasBakedAssets: v.has_baked_assets,
  };
}

interface WireComment {
  id: string;
  template_id: string;
  template_version_id: string | null;
  comment_type: CommentType;
  comment_text: string;
  created_by: string | null;
  created_by_role: string | null;
  created_at: string;
}

/** Map a cloud `CommentOut` to the client `ReviewComment` view-model (spec 018). */
function wireCommentToView(c: WireComment): ReviewComment {
  return {
    id: c.id,
    templateId: c.template_id,
    templateVersionId: c.template_version_id,
    commentType: c.comment_type,
    commentText: c.comment_text,
    createdBy: c.created_by,
    createdByRole: c.created_by_role,
    createdAt: c.created_at,
  };
}

// --- Dashboard wire shapes (010) -------------------------------------------
// The cloud `dashboard.py` serializers (`_job_summary`, `_hb`, `exports`,
// `job_detail`) emit snake_case with ISO-string timestamps. The telemetry
// view-models (telemetry/types.ts) are camelCase with epoch-ms numbers, so
// each dashboard read MUST be normalized — exactly like `wireVersionToView`.

/** Raw cloud `_job_summary` row (dashboard.py). */
interface WireJobSummary {
  id: string;
  tenant_id: string;
  device_id: string;
  kind: string;
  status: JobStatus;
  design_count: number;
  view_count: number;
  duration_ms: number | null;
  engine_version: string | null;
  queued_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

/** Raw cloud `_hb` heartbeat row (dashboard.py). */
interface WireHeartbeat {
  device_id: string;
  tenant_id: string;
  online: boolean;
  active_job_id: string | null;
  /** Fraction of the running job, 0–1 (never a percentage); null when idle. */
  progress: number | null;
  app_version: string | null;
  engine_version: string | null;
  server_ts: string | null;
}

/** Raw cloud export row (dashboard.py `exports()`). */
interface WireExport {
  id: string;
  design_slug: string;
  view: ExportRecord["view"];
  format: ExportRecord["format"];
  counts: number | null;
  status: ExportRecord["status"];
  local_ref: string | null;
  created_at: string | null;
}

/** Raw cloud trace row (dashboard.py `job_detail`). */
interface WireTrace {
  phase: string;
  level: TraceLevel;
  start_ms: number | null;
  end_ms: number | null;
  message: string;
}

/** Raw cloud `activity()` envelope — note: NO exports (merged client-side). */
interface WireActivity {
  jobs: WireJobSummary[];
  heartbeats: WireHeartbeat[];
}

/** Raw cloud `job_detail` envelope. */
interface WireJobDetail {
  job: WireJobSummary;
  items: unknown[];
  traces: WireTrace[];
}

/**
 * @deprecated Use `parseServerTs`. Kept because ~24 call sites in ObservabilitySection
 * and the platform tests import this name; the implementation is the same function.
 */
export const isoToMs = parseServerTs;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** The error body, whatever shape it is.
 *
 * `res.json().catch(() => undefined)` dropped every non-JSON body on the floor: a proxy's
 * HTML 502/504 became "POST /api/… -> 502" with no reason attached, in the log and in the
 * banner (AUD-4 IMP-6). Text is truncated because it can be a full error page. */
async function readErrorBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get("content-type") ?? "";
  let text: string;
  try {
    text = await res.text();
  } catch {
    return undefined;
  }
  if (!text) return undefined;
  if (contentType.includes("json") || text.trimStart().startsWith("{")) {
    try {
      return JSON.parse(text);
    } catch {
      /* fall through to the text form */
    }
  }
  return { detail: text.slice(0, 2000), contentType };
}

/**
 * Map a cloud `_job_summary` to the `JobRecord` view-model the dashboard reads
 * (jobId/designCount/durationMs + timestamps). The summary is a projection, so
 * fields it doesn't carry (templateId/templateVersionId/views/items) get empty
 * defaults — the full per-item detail is fetched on demand via `dashboardJob`.
 */
function wireJobToView(j: WireJobSummary): JobRecord {
  return {
    jobId: j.id,
    tenantId: j.tenant_id,
    deviceId: j.device_id,
    templateId: "",
    templateVersionId: "",
    engineVersion: j.engine_version ?? "",
    designCount: j.design_count,
    views: [],
    status: j.status,
    queuedAt: isoToMs(j.queued_at),
    startedAt: j.started_at ? isoToMs(j.started_at) : undefined,
    completedAt: j.completed_at ? isoToMs(j.completed_at) : undefined,
    durationMs: j.duration_ms ?? undefined,
    items: [],
    position: 0,
  };
}

/** Map a cloud `_hb` row to the `HeartbeatPayload` view-model (server_ts → ts). */
function wireHeartbeatToView(h: WireHeartbeat): HeartbeatPayload {
  return {
    deviceId: h.device_id,
    tenantId: h.tenant_id,
    online: h.online,
    activeJobId: h.active_job_id ?? undefined,
    progress: h.progress ?? undefined,
    appVersion: h.app_version ?? "",
    engineVersion: h.engine_version ?? "",
    ts: isoToMs(h.server_ts),
  };
}

/** Map a cloud export row to the `ExportRecord` view-model (design_slug→designName, counts→count). */
function wireExportToView(e: WireExport): ExportRecord {
  return {
    batchId: e.id,
    tenantId: "",
    designName: e.design_slug,
    view: e.view,
    format: e.format,
    count: e.counts ?? 0,
    status: e.status,
    ref: e.local_ref ?? undefined,
  };
}

/** Map a cloud trace row to the `TraceEvent` view-model (start_ms/end_ms → startedAt/endedAt). */
function wireTraceToView(jobId: string, t: WireTrace): TraceEvent {
  return {
    jobId,
    phase: t.phase,
    startedAt: t.start_ms ?? 0,
    endedAt: t.end_ms ?? undefined,
    level: t.level,
    message: t.message,
  };
}

/** Raw cloud unified log row (dashboard.py `log()`). */
interface WireLogEntry {
  id: string;
  kind: LogEntry["kind"];
  status: string;
  owner_email: string | null;
  when: string | null;
  error: string | null;
}

/** Map a cloud unified-log row to the `LogEntry` view-model (when ISO → epoch ms). */
function wireLogToView(r: WireLogEntry): LogEntry {
  return {
    id: r.id,
    kind: r.kind,
    status: r.status,
    ownerEmail: r.owner_email ?? null,
    when: isoToMs(r.when),
    error: r.error ?? null,
  };
}

/** Server-resolved display fields, shared by TemplateSummary + TemplateFull (P3). */
interface WireTemplateDisplay {
  category: string;
  brand: string | null;
  model: string | null;
  style: string | null;
  views: string[];
  output_format: string;
  /** The current version has a `mockup_only` view. An older server omits it. */
  mockup_only?: boolean;
}

/** Raw cloud `TemplateSummary` (catalog/schemas.py) — snake_case over the wire. */
interface WireTemplateSummary extends WireTemplateDisplay {
  id: string;
  name: string;
  product_type: string;
  phone_model_id: string | null;
  case_style_id: string | null;
  base_type?: string | null;
  code?: string | null;
  origin: string;
  is_library: boolean;
  archived: boolean;
  status: TemplateStatus;
  current_version_id: string | null;
  /** The CURRENT version's number. Optional: an older server (pre-WS-D1) omits it, and
   *  the row then falls back to 1 — which is what every row used to claim
   *  unconditionally, so a template on version 5 read "Version 1" in both index tables. */
  version_number?: number | null;
  /** WS-P: whether the CURRENT version published a cross-device mock-up. Optional — a
   *  pre-WS-P server omits it and the row must read `undefined` (unknown), not `false`. */
  has_baked_assets?: boolean;
  preview_ref?: string | null;
  // Change signal: the current version's write token (bumps on every version write, incl.
  // baked-asset updates) — lets autoSync detect same-version-id content changes from rows.
  current_version_token?: string | null;
  // `brand` is inherited from `WireTemplateDisplay` above — WS-C's
  // `service.py::LabelResolver.brand_label`. It is `string | null` rather than absent, and
  // a pre-WS-C server that omits it lands on the client's phone-model-catalog fallback via
  // `brandOf`, which is where WS-B resolves it anyway.
  /** The HEAD (newest) version's number + status. NOT emitted by any server yet — WS-B
   *  defines the wire names so whichever wave lands first, nothing breaks. Both members
   *  must be present for the pair to count; a half-sent pair is treated as absent. */
  head_version_number?: number | null;
  head_status?: TemplateStatus | null;
}

/** The head version pair, or null unless BOTH members arrived. */
function wireHeadVersion(
  t: Pick<WireTemplateSummary, "head_version_number" | "head_status">,
): ProductTemplate["headVersion"] {
  return typeof t.head_version_number === "number" && t.head_status
    ? { versionNumber: t.head_version_number, status: t.head_status }
    : null;
}

/** Raw cloud `TemplatePage` (catalog/schemas.py) — the envelope the listings return when
 *  the caller sends `limit` or `cursor`. The route returns a BARE ARRAY otherwise, because
 *  v0.2.89 desktops call `.map()` straight on the body and there is no way to make them
 *  stop polling. WS-B's listings send neither `limit` nor `cursor`, so the tolerance below
 *  is what lets the two waves land in either order. */
interface WireTemplatePage {
  items: WireTemplateSummary[];
  next_cursor: string | null;
  limit: number;
}

/** One page of a template listing. */
export interface TemplatePage {
  items: ProductTemplate[];
  /** Pass back as `cursor` for the next page; null when this was the last one. */
  nextCursor: string | null;
  limit: number;
}

/**
 * Accept BOTH listing shapes. The cloud answers with a bare array when no `limit`/`cursor`
 * was sent (so a v0.2.89 desktop keeps working) and with the envelope otherwise — this is
 * the one place that fork is handled, so no caller has to care which server it is talking
 * to, in either direction.
 */
function templateRows(
  body: WireTemplateSummary[] | WireTemplatePage | null | undefined,
): WireTemplateSummary[] {
  if (Array.isArray(body)) return body;
  return body?.items ?? [];
}

/** Raw cloud `TemplateFull` (catalog/schemas.py) — snake_case over the wire. */
interface WireTemplateFull extends WireTemplateDisplay {
  id: string;
  name: string;
  product_type: string;
  phone_model_id: string | null;
  case_style_id: string | null;
  base_type?: string | null;
  code?: string | null;
  origin: string;
  is_library: boolean;
  archived: boolean;
  // `brand` is inherited from `WireTemplateDisplay` — the detail fetch carries it too, so
  // opening a template does not lose the column the row had.
  url_rules: Record<string, unknown>;
  current_version: WireVersionOut | null;
  current_version_id: string | null;
  // Hosted preview thumbnail (mirrors TemplateSummary) — the detail fetch must carry it
  // so a second device / customer has a preview URL even before local baked-asset hydration.
  preview_ref?: string | null;
}

/** Map the wire display fields (snake_case) to the `TemplateDisplay` view-model. */
function wireDisplay(t: WireTemplateDisplay): TemplateDisplay {
  return {
    category: t.category,
    brand: t.brand ?? null,
    model: t.model,
    style: t.style,
    views: t.views ?? [],
    outputFormat: t.output_format,
  };
}

/**
 * Map a cloud `TemplateSummary` (list row) to the `ProductTemplate` view-model.
 * The summary carries no nested version object — only a flat `status` +
 * `current_version_id` — so we synthesize a minimal `currentVersion` from those
 * (the panel reads `status`/`versionNumber`; the full version PAYLOAD is fetched
 * on demand via `getTemplate`).
 */
function wireSummaryToTemplate(t: WireTemplateSummary): ProductTemplate {
  return {
    templateId: t.id,
    tenantId: "",
    name: t.name,
    phoneModelId: t.phone_model_id ?? "",
    caseStyleId: t.case_style_id ?? "",
    baseType: t.base_type ?? null,
    code: t.code ?? null,
    display: wireDisplay(t),
    currentVersion: {
      versionId: t.current_version_id ?? "",
      // The row carries the real number now; 1 is the pre-WS-D1 fallback (what this
      // used to hardcode) for an older server that doesn't send it.
      versionNumber: t.version_number ?? 1,
      status: t.status,
      // The write token IS meaningful on summaries now (change signal for device sync);
      // an older server that doesn't send it yields "" — token comparison then no-ops.
      versionToken: t.current_version_token ?? "",
      // WS-P: the list row can now say "Mock-up not published" about the CLOUD without
      // asking the machine that baked it. Absent (older server) stays absent, never `false`.
      hasBakedAssets: t.has_baked_assets,
    },
    previewRef: t.preview_ref ?? null,
    // WS-B B-4. `tenantId` deliberately stays "" above: `TemplateSummary` carries no tenant
    // id and inventing one here would be a lie (F-28, third clause — see the WS-C ticket).
    brand: t.brand?.trim() || null,
    origin: t.origin ?? "own",
    isLibrary: t.is_library ?? false,
    headVersion: wireHeadVersion(t),
    ...(t.mockup_only ? { mockupOnly: true } : {}),
  };
}

/**
 * Map a cloud `TemplateFull` (detail) to the `ProductTemplate` view-model,
 * reusing `wireVersionToView` for the nested `current_version` so the render
 * payload survives. The server's `url_rules` is its sku/path-template dict — a
 * SEPARATE concern from display (P3), so it is no longer carried on the view-model;
 * the UI reads the explicit `display` fields instead.
 */
function wireFullToTemplate(t: WireTemplateFull): ProductTemplate {
  const v = t.current_version
    ? wireVersionToView(t.current_version)
    : {
        versionId: t.current_version_id ?? "",
        versionNumber: 1,
        status: (t.archived ? "Archived" : "Draft") as TemplateStatus,
        versionToken: "",
      };
  return {
    templateId: t.id,
    tenantId: "",
    name: t.name,
    phoneModelId: t.phone_model_id ?? "",
    caseStyleId: t.case_style_id ?? "",
    baseType: t.base_type ?? null,
    code: t.code ?? null,
    display: wireDisplay(t),
    currentVersion: v,
    previewRef: t.preview_ref ?? null,
    // WS-B B-4: the detail fetch already receives these; it just never mapped them, so
    // opening a template lost the fields the row had. `TemplateFull` has no head pair —
    // `getTemplate` returns the head version anyway.
    brand: t.brand?.trim() || null,
    origin: t.origin ?? "own",
    isLibrary: t.is_library ?? false,
    ...(t.mockup_only ? { mockupOnly: true } : {}),
  };
}

/** Wire shape of a generation job (cloud `_out`). */
interface WireGenJob {
  id: string;
  job_type: string;
  template_id: string | null;
  view: string;
  status: GenerationJobView["status"];
  priority: number;
  est_cost_usd: number;
  error: string | null;
  error_detail?: string | null;
  customer_id: string;
  tenant_id: string | null;
  owner_email?: string | null;
  result_url: string | null;
  pass?: "clear" | "magenta" | null;
  handoff_step?: GenerationHandoffStep | null;
  published_template_id?: string | null;
  clear_url?: string | null;
  thumb_url?: string | null;
  clear_thumb_url?: string | null;
  clear_ref?: string | null;
  original_url?: string | null;
  can_retry_magenta?: boolean;
  checkNotes?: string[];
  check_notes?: string[];
  created_at: string | null;
  finished_at: string | null;
  /** Product detail fields carried from the Mockups enqueue form (v0.2.46+, nullable). */
  product_type?: string | null;
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  print_area?: GenerationPrintArea | null;
  phone_model_id?: string | null;
  case_style_id?: string | null;
  base_type?: string | null;
  mockup_only?: boolean;
  run_id?: string | null;
  executor?: GenerationJobExecutor;
  resolution?: string | null;
  quality?: string | null;
  credits?: number | null;
  /** Device-run inputs — single-job GET only. The server sends them camelCased; the
   *  snake_case spellings are read too, so a cloud that sent those still works. */
  inputUrl?: string | null;
  productUrl?: string | null;
  viewUrl?: string | null;
  /** Every case reference, in order; null when the cloud did not compute them. */
  inputUrls?: string[] | null;
  promptSystem?: string | null;
  promptUser?: string | null;
  promptText?: string | null;
  promptStatus?: "ready" | "unavailable" | null;
}

function wireJobsSummary(w?: { total: number; by_status: Record<string, number> }): GenerationJobsSummary {
  return { total: w?.total ?? 0, byStatus: w?.by_status ?? {} };
}

/** Map a wire generation job (snake_case) to the camelCase view-model. */
function wireGenJob(w: WireGenJob): GenerationJobView {
  return {
    id: w.id,
    jobType: w.job_type,
    templateId: w.template_id,
    view: w.view,
    status: w.status,
    priority: w.priority,
    estCostUsd: w.est_cost_usd,
    error: w.error,
    errorDetail: w.error_detail ?? null,
    customerId: w.customer_id,
    tenantId: w.tenant_id,
    ownerEmail: w.owner_email ?? null,
    resultUrl: w.result_url,
    pass: w.pass ?? null,
    handoffStep: w.handoff_step ?? null,
    publishedTemplateId: w.published_template_id ?? null,
    clearUrl: w.clear_url ?? null,
    thumbUrl: w.thumb_url ?? null,
    clearThumbUrl: w.clear_thumb_url ?? null,
    clearRef: w.clear_ref ?? null,
    originalUrl: w.original_url ?? null,
    canRetryMagenta: w.can_retry_magenta ?? false,
    checkNotes: w.checkNotes ?? w.check_notes ?? [],
    createdAt: w.created_at,
    finishedAt: w.finished_at,
    productType: w.product_type ?? null,
    brand: w.brand ?? null,
    model: w.model ?? null,
    name: w.name ?? null,
    printArea: w.print_area ?? null,
    phoneModelId: w.phone_model_id ?? null,
    caseStyleId: w.case_style_id ?? null,
    baseType: w.base_type ?? null,
    mockupOnly: w.mockup_only ?? false,
    runId: w.run_id ?? null,
    executor: w.executor ?? "cloud",
    resolution: w.resolution ?? null,
    quality: w.quality ?? null,
    credits: w.credits ?? null,
    inputUrl: w.inputUrl ?? null,
    productUrl: w.productUrl ?? null,
    viewUrl: w.viewUrl ?? null,
    inputUrls: w.inputUrls ?? [],
    promptSystem: w.promptSystem ?? null,
    promptUser: w.promptUser ?? null,
    promptText: w.promptText ?? null,
    promptStatus: w.promptStatus ?? null,
  };
}

/** Global cloud-activity signal: dispatches a `genz:cloud-activity` window event
 *  ({active}) whenever a request is in flight, so the UI can show a live indicator. */
let cloudInflight = 0;
export function notifyCloudActivity(delta: number): void {
  cloudInflight = Math.max(0, cloudInflight + delta);
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(new CustomEvent("genz:cloud-activity", { detail: { active: cloudInflight > 0 } }));
  }
}

/** Report cloud reachability from the last request: a thrown fetch / 5xx → not
 *  reachable (the indicator goes red even if the browser thinks it's online); a
 *  2xx/4xx response means the cloud answered → reachable. */
export function notifyCloudHealth(ok: boolean): void {
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(new CustomEvent("genz:cloud-health", { detail: { ok } }));
  }
}

/** Request paths NEVER reported to the observability hook: the ingest path itself (an
 * amplifying flush loop) and the 5-second heartbeat (720 rows/hour of pure noise — and
 * 720 error rows/hour when the cloud is down, evicting genuine errors from every buffer). */
/**
 * What a token refresh actually did. Reported rather than inferred: the caller used to
 * guess from `access_token !== before`, which mis-read both a re-issued identical token
 * (real success) and a 2xx with no token (real failure).
 *
 * - `refreshed`  — new tokens are installed; retry the request.
 * - `transient`  — offline / 5xx / timeout / malformed 2xx. Tokens survive, unrotated.
 * - `dead`       — the cloud itself rejected the chain with a 401. Tokens cleared
 *                  and `onAuthExpired` already fired exactly once.
 * - `superseded` — the identity changed while the refresh was in flight (logout, or a
 *                  login as another account). The result is discarded, not applied.
 */
type RefreshOutcome = "refreshed" | "transient" | "dead" | "superseded";

const UNOBSERVED_PATHS = new Set(["/api/telemetry/events", "/api/telemetry/heartbeat"]);

/** Per-request correlation id (gadget.dev's trace_id idea): sent as X-Request-Id, stamped
 *  into the client's own network event, and echoed by the cloud into any error it records. */
export function makeRequestId(): string {
  return `rq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Run `fn` holding the origin-wide refresh lock, so the tabs of one browser present a refresh
 * token one at a time: a third racing presentation is reuse, and reuse ends the chain for
 * every tab. Without Web Locks it runs unlocked, the per-tab behaviour.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  return locks ? locks.request("genz-refresh", fn) : fn();
}

export class JsonClient {
  private baseUrl: string;
  private tokens?: AuthTokens;
  private refreshing?: Promise<RefreshOutcome>;
  /**
   * Set while a deliberate sign-out lands. The server ends the session's access token at once
   * (AR-12), so a request still in flight 401s and its refresh is refused: that is the
   * sign-out arriving, not an expiry to announce. Cleared by the next sign-in.
   */
  private signingOut = false;
  /** AR-08: aborted at the end of each session, so no request outlives the account it ran for. */
  private session = new AbortController();
  private readonly onAuthExpired?: () => void;
  private readonly onTokensChanged?: (tokens: AuthTokens | undefined) => void;
  private readonly readStoredTokens?: () => AuthTokens | undefined;
  private readonly doFetch: typeof fetch;
  private readonly requestTimeoutMs: number;
  private readonly retryDelaysMs: number[];
  private readonly onRequestObserved?: JsonClientOptions["onRequestObserved"];
  private readonly onClockSkew?: JsonClientOptions["onClockSkew"];
  /** MIN-17: the skew is measured once and never again — this is a diagnosis, not a metric. */
  private clockSkewReported = false;

  constructor(opts: JsonClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.onAuthExpired = opts.onAuthExpired;
    this.onTokensChanged = opts.onTokensChanged;
    this.readStoredTokens = opts.readStoredTokens;
    this.doFetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.requestTimeoutMs = opts.requestTimeoutMs ?? 180_000;
    this.retryDelaysMs = opts.retryDelaysMs ?? [250, 1000];
    this.onRequestObserved = opts.onRequestObserved;
    this.onClockSkew = opts.onClockSkew;
  }

  /**
   * Measure this machine against the server, once per session (MIN-17).
   *
   * The source is the HTTP `Date` header, which every response carries and which is
   * second-resolution — ample for a diagnosis whose thresholds are a minute and whose
   * consequences start in the minutes. A response without one (or with an unparseable one)
   * simply leaves the probe unfired: there is nothing to report and nothing to invent.
   */
  private measureClockSkew(res: Response): void {
    if (this.clockSkewReported || !this.onClockSkew) return;
    const header = res.headers?.get?.("Date");
    if (!header) return;
    const serverMs = Date.parse(header);
    if (!Number.isFinite(serverMs)) return;
    this.clockSkewReported = true;
    try {
      this.onClockSkew(Date.now() - serverMs);
    } catch {
      // A diagnostic must never break the request it rode in on. The flag is already set, so
      // a throwing handler is not retried on every later response either.
    }
  }

  /** Set tokens and notify the persistence layer (login / refresh / logout). */
  private applyTokens(tokens: AuthTokens | undefined): void {
    this.tokens = tokens;
    this.onTokensChanged?.(tokens);
  }

  setTokens(tokens: AuthTokens | undefined): void {
    // Sync from a restored/persisted session WITHOUT re-notifying (avoids a write loop).
    this.tokens = tokens;
  }
  getTokens(): AuthTokens | undefined {
    return this.tokens;
  }

  /** Cancel every request still in flight for the session that just ended. */
  endSession(): void {
    this.session.abort();
    this.session = new AbortController();
  }

  /** fetch wrapper that flags cloud activity (for the live connection indicator). */
  private async trackedFetch(url: string, opts: RequestInit, session?: AbortSignal): Promise<Response> {
    if (session?.aborted) throw new SignedOutError();
    notifyCloudActivity(1);
    // Hard timeout: race the fetch against a timer that aborts + rejects, so a stalled
    // cloud/provider can never leave the UI hanging forever. We reject explicitly (not
    // just abort) because a non-cooperative fetch impl may ignore the abort signal.
    const ctl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let onSignOut = () => {};
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        ctl.abort();
        reject(new Error(`Request timed out after ${this.requestTimeoutMs}ms`));
      }, this.requestTimeoutMs);
      onSignOut = () => {
        reject(new SignedOutError());
        ctl.abort();
      };
    });
    session?.addEventListener("abort", onSignOut);
    try {
      const res = await Promise.race([
        this.doFetch(url, { ...opts, signal: ctl.signal }),
        timeout,
      ]);
      notifyCloudHealth(res.status < 500); // 2xx/4xx = cloud answered; 5xx = unhealthy
      return res;
    } catch (e) {
      if (session?.aborted) throw new SignedOutError();
      notifyCloudHealth(false); // network error / timeout / unreachable
      throw e;
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      session?.removeEventListener("abort", onSignOut);
      notifyCloudActivity(-1);
    }
  }

  // --- core request with one transparent refresh+retry on 401 ---------------
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: {
      auth?: boolean;
      raw?: boolean;
      multipart?: FormData;
      /** Survive a document navigation / window close (see `heartbeat`). */
      keepalive?: boolean;
      /** Opt OUT of the idempotent-read retry below. */
      retry?: boolean;
      /** Not cancelled by `endSession`: a paid device run and the sign-out itself must settle. */
      keepOnSignOut?: boolean;
    },
  ): Promise<T> {
    const session = init?.keepOnSignOut ? undefined : this.session.signal;
    const live = () => { if (session?.aborted) throw new SignedOutError(); };
    const auth = init?.auth ?? true;
    // Proactively refresh an ALREADY-EXPIRED access token before the first send, so a cold
    // start with a stale-but-refreshable persisted session doesn't fire a guaranteed-401
    // request (and log a console error) before the reactive 401-refresh below kicks in. The
    // refresh call itself uses { auth: false }, so this never recurses; concurrent callers
    // share the one in-flight refresh via ensureRefreshed().
    if (auth && this.tokens?.refresh_token && accessTokenExpired(this.tokens.access_token)) {
      await this.ensureRefreshed();
    }
    // Only a request that actually SENT a token can have its session "expire". A
    // 401 on a request with no token — demo mode (local-only session, no client
    // token) or a background telemetry heartbeat firing before login — means
    // "not authorized", not "your session expired", so it must NOT clear the
    // session. Without this, the 5s telemetry heartbeat 401s against the cloud and
    // bounces demo users straight back to the login screen.
    const sentAuth = auth && !!this.tokens;
    const headers: Record<string, string> = {};
    let payload: BodyInit | undefined;

    if (init?.multipart) {
      payload = init.multipart;
    } else if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
    if (auth && this.tokens) headers.Authorization = `Bearer ${this.tokens.access_token}`;
    // One id per LOGICAL request — retries reuse it, and the cloud echoes it into any error
    // it records, so a single search connects this client call to its server-side traceback.
    const requestId = makeRequestId();
    headers["X-Request-Id"] = requestId;
    const observe = (r: {
      status?: number;
      ok: boolean;
      error?: string;
      errorBody?: unknown;
    }) => {
      // Observation moved here from trackedFetch: only request() knows the FINAL outcome
      // (after 401 refresh+retry — the intermediate 401s were pure noise) and the parsed
      // error body. Telemetry/heartbeat paths stay unobserved (amplification loop).
      if (!this.onRequestObserved || UNOBSERVED_PATHS.has(path)) return;
      try {
        this.onRequestObserved({
          method: method.toUpperCase(),
          urlPath: path,
          durationMs: Date.now() - observeStart,
          requestId,
          ...r,
        });
      } catch {
        // Hook errors must never break the request path.
      }
    };
    const observeStart = Date.now();
    // What THIS request put on the wire — not "what the tokens hold now". A concurrent
    // refresh can rotate them between here and the 401 handler below.
    const sentAccess = this.tokens?.access_token;

    const url = `${this.baseUrl}${path}`;
    // Every attempt (first send and the 401-refresh retries) goes through `send`, so a network
    // failure mid-retry is observed exactly like one on the first try. Reads `headers` at call
    // time — the retry path rewrites Authorization before re-sending.
    const fire = (): Promise<Response> => this.trackedFetch(
      url, { method, headers, body: payload, keepalive: init?.keepalive }, session,
    );
    const send = async (): Promise<Response> => {
      try {
        return await fire();
      } catch (e) {
        if (!isSignedOut(e)) observe({ ok: false, error: e instanceof Error ? e.message : String(e) });
        throw e;
      }
    };

    // AUD-4 IMP-14: a one-second blip surfaced as a hard error and a lost edit. Retry
    // ONLY what is safe to send twice — GET/HEAD — and only for a gateway-class answer or
    // a thrown fetch. A 500 is a deterministic server bug (retrying it just doubles the
    // load), and a POST/PATCH/DELETE is never retried here: without an idempotency key
    // the cloud has no way to tell a retry from a second submission.
    //
    // Every attempt reuses the one `X-Request-Id`, so a support search still finds a
    // single logical request, and only the FINAL failure is observed — an observation per
    // attempt would report transient blips as errors the user never saw.
    const idempotent = method === "GET" || method === "HEAD";
    const canRetry = idempotent && init?.retry !== false;
    const retryable = (r: Response | null) =>
      r === null || r.status === 502 || r.status === 503 || r.status === 504;

    let res: Response | null = null;
    let thrown: unknown;
    try {
      res = canRetry ? await fire() : await send();
    } catch (e) {
      if (!canRetry) throw e; // `send` already observed it
      thrown = e;
    }

    if (canRetry && retryable(res) && !isSignedOut(thrown)) {
      for (const base of this.retryDelaysMs) {
        await sleep(base * (0.8 + Math.random() * 0.4));
        try {
          res = await fire();
          thrown = undefined;
        } catch (e) {
          res = null;
          thrown = e;
          if (isSignedOut(e)) break;
          continue;
        }
        if (!retryable(res)) break;
      }
    }

    if (res === null) {
      live();
      const message = thrown instanceof Error ? thrown.message : String(thrown);
      observe({ ok: false, error: message });
      throw thrown;
    }

    let transientRefresh = false;
    if (res.status === 401 && auth && this.tokens?.refresh_token) {
      if (this.tokens.access_token !== sentAccess) {
        // Another request already refreshed while this one was in flight, so the 401 is
        // simply stale. Retry with the CURRENT token first: calling ensureRefreshed() here
        // burned a second round-trip and rotated the refresh chain again for every
        // straggler that happened to be mid-flight during a rotation.
        headers.Authorization = `Bearer ${this.tokens.access_token}`;
        res = await send();
      } else {
        // The refresh reports its OUTCOME rather than leaving us to infer one. Inferring
        // "did it work?" from `access_token !== before` was wrong in both directions: a
        // server that re-issued the SAME still-valid token read as a failure (no retry, no
        // expiry signal — the request just died), and a 200 carrying no token at all read
        // as success.
        const outcome = await this.ensureRefreshed();
        if (outcome === "refreshed" && this.tokens) {
          headers.Authorization = `Bearer ${this.tokens.access_token}`;
          res = await send();
        } else if (outcome !== "dead") {
          // TRANSIENT (offline/5xx/timeout) or SUPERSEDED (the identity changed under us).
          // The tokens survive, unrotated: re-sending the same stale header would just
          // produce a second guaranteed 401 that the block below converts into
          // onAuthExpired — a flaky network during a save became a hard logout (sync audit
          // C12). Fail THIS request only and keep the session for the next attempt.
          transientRefresh = true;
        }
        // "dead": ensureRefreshed has already cleared the tokens and fired onAuthExpired.
      }
    }

    live();
    if (!res.ok) {
      const errBody = await readErrorBody(res);
      live();
      observe({ status: res.status, ok: false, errorBody: errBody });
      // A 401 during a TRANSIENT refresh outage is not expiry — the session is refreshable;
      // ensureRefreshed() itself fires onAuthExpired on a definitive 401 refresh answer.
      // `this.tokens` is the third condition: when ensureRefreshed() got that 401 it already
      // cleared them AND fired onAuthExpired. Without this guard every
      // request that was in flight at that moment fired it again — one expiry became N+1
      // concurrent workspace purges racing each other.
      if (res.status === 401 && sentAuth && !transientRefresh && this.tokens && !this.signingOut) {
        this.onAuthExpired?.();
      }
      throw new HttpError(res.status, `${method} ${path} -> ${res.status}`, errBody);
    }
    observe({ status: res.status, ok: true });
    // MIN-17: the first AUTHED response is the measurement — an unauthenticated one can be
    // served from a cache or an edge that carries its own `Date`.
    if (sentAuth) this.measureClockSkew(res);
    if (res.status === 204 || init?.raw) return undefined as T;
    const data = await res.json().catch(() => undefined);
    live();
    return data as T;
  }

  private async ensureRefreshed(): Promise<RefreshOutcome> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = withRefreshLock(() => this.refreshOnce()).finally(() => {
      this.refreshing = undefined;
    });
    return this.refreshing;
  }

  /** Another tab rotated this chain while we waited for the lock: take its tokens. */
  private adoptRotatedTokens(chain: string): boolean {
    const stored = this.readStoredTokens?.();
    if (!stored?.refresh_token || stored.refresh_token === chain) return false;
    if (accessTokenExpired(stored.access_token)) return false;
    this.applyTokens(stored);
    return true;
  }

  private async refreshOnce(): Promise<RefreshOutcome> {
    // The chain we set out to refresh. Everything below is conditional on it still being
    // the current one when we come back: a refresh can be in flight across a logout or a
    // login as a DIFFERENT account, and `applyTokens` used to write unconditionally —
    // re-installing the previous account's tokens on the shared client afterwards.
    const chain = this.tokens?.refresh_token;
    if (!chain) return "superseded"; // signed out while this tab waited for the lock
    if (this.adoptRotatedTokens(chain)) return "refreshed";
    try {
      const next = await this.request<AuthTokens>(
        "POST",
        "/api/auth/refresh",
        { refresh_token: this.tokens!.refresh_token },
        { auth: false },
      );
      if (this.tokens?.refresh_token !== chain) return "superseded";
      if (!next?.access_token) {
        // A 2xx with an empty or unparsable body. `request` returns undefined for those,
        // and applying it set `tokens = undefined` on a SUCCESSFUL refresh — with no
        // onAuthExpired, so the app stayed "logged in" while every later request went out
        // unauthenticated, 401'd with sentAuth false, and never bounced anyone to login.
        // Treat it as transient: the session survives and the next attempt can recover.
        return "transient";
      }
      this.applyTokens(next);
      return "refreshed";
    } catch (e) {
      // ONLY a definitive auth rejection means the session is dead. The old bare catch
      // treated EVERY failure — Wi-Fi not up after wake, DNS, a 5xx, the fetch timeout —
      // as expiry and wiped the persisted session: the "desktop keeps logging me out" bug
      // (a cold start ALWAYS refreshes, so one offline launch was enough). Transient
      // failures keep the tokens; the next request retries.
      //
      // And "definitive" means a 401, not a 401-or-403.
      //
      // `cloud/app/auth/router.py`'s refresh raises `unauthorized()` — a 401, always; it has
      // no 403 path. So every 403 that reached here came from something IN FRONT of the
      // cloud: a reverse proxy, a WAF, a Cloudflare challenge, a corporate TLS-inspecting
      // gateway, a CDN rule. Each of those is transient, and each used to cost a full
      // workspace purge (AUD-8 IMP-15). Narrowing costs nothing: a genuinely dead chain
      // always 401s.
      //
      // 429 is deliberately absent from BOTH lists and always was — WS-G's limiter returns
      // 429 and buckets /api/auth/* at `settings.auth_rate_limit_per_min`, so limiter
      // pressure degrades to `transient`, which is correct.
      if (e instanceof HttpError && e.status === 401) {
        if (this.tokens?.refresh_token !== chain) return "superseded";
        this.applyTokens(undefined);
        if (!this.signingOut) this.onAuthExpired?.();
        return "dead";
      }
      return "transient";
    }
  }

  // --- Auth (006) -----------------------------------------------------------
  /**
   * Sign in.
   *
   * `persistent` is D-35's "keep me signed in on this device": the cloud then issues a
   * 180-day SLIDING chain capped at 365 days from first login, instead of the hard 30-day
   * one. It defaults to **false**, so every existing caller — and the web build, where a
   * browser profile is not a trusted device — keeps today's behaviour with no change.
   *
   * `deviceLabel` is a human name for Settings → Signed-in devices, so the owner can
   * recognise a session and end it from somewhere else. Never a fingerprint, never an IP.
   */
  async login(
    email: string,
    password: string,
    opts?: { persistent?: boolean; deviceLabel?: string },
  ): Promise<{ tokens: AuthTokens; role: Role }> {
    const body: Record<string, unknown> = { email, password };
    // Only sent when asked for, so the request an unchanged caller makes is unchanged.
    if (opts?.persistent) body.persistent = true;
    if (opts?.deviceLabel) body.device_label = opts.deviceLabel;
    const r = await this.request<LoginResponse>("POST", "/api/auth/login", body, { auth: false });
    const tokens: AuthTokens = {
      access_token: r.access_token,
      refresh_token: r.refresh_token,
      expires_in: r.expires_in,
      // Carried so the session store — and therefore a restart — still knows when the CHAIN
      // dies (IMP-13). The refresh path applies the whole response, so it needs no equivalent.
      chain_expires_at: r.chain_expires_at,
      persistent: r.persistent,
    };
    this.signingOut = false;
    this.applyTokens(tokens);
    return { tokens, role: r.role };
  }

  async register(email: string, password: string, tenantName?: string): Promise<{ customer_id: string }> {
    return this.request("POST", "/api/auth/register", { email, password, tenant_name: tenantName }, { auth: false });
  }

  /** The signed-in devices on this account (WS-Q Q-5). Ids, labels and timestamps only. */
  async listSessions(): Promise<SignedInDevice[]> {
    return (await this.request<SignedInDevice[]>("GET", "/api/auth/sessions")) ?? [];
  }

  /**
   * End ONE signed-in device. Revoking the chain this client is on is allowed and behaves
   * exactly like a sign-out server-side — the caller is responsible for the local half.
   */
  async revokeSession(chainId: string): Promise<void> {
    const ending = !!this.tokens && decodeAccessToken(this.tokens.access_token)?.chain === chainId;
    if (ending) this.signingOut = true;
    try {
      await this.request("POST", `/api/auth/sessions/${encodeURIComponent(chainId)}/revoke`);
    } catch (e) {
      if (ending) this.signingOut = false; // not signed out: a later expiry is real again
      throw e;
    }
  }

  async logout(): Promise<void> {
    // `/api/auth/logout` takes a REQUIRED `RefreshReq` body (auth/router.py). This posted
    // nothing, so every sign-out 422'd — and the `.catch()` swallowed it. The refresh token
    // therefore stayed LIVE on the server for the rest of its 30-day chain: "sign out" only
    // ever cleared this device's copy. Send the token so the server can actually revoke it.
    //
    // `auth: false` is deliberate: with the bearer path on, an expired access token would
    // first trigger a transparent refresh, ROTATING the refresh token — and we would then
    // revoke the superseded one while the freshly-minted chain stayed alive. The route
    // authenticates on the body token alone, so no bearer is needed.
    // `keepalive` for the same reason the telemetry heartbeat has it (WS-G): a sign-out fired
    // while the app is quitting is aborted by the closing window, and the refresh token then
    // stays LIVE server-side for the rest of its chain — the very thing this call exists to
    // prevent. The body is a few hundred bytes, far under the 64KB keepalive limit.
    this.signingOut = true;
    const refreshToken = this.tokens?.refresh_token;
    if (refreshToken) {
      await this.request<void>(
        "POST", "/api/auth/logout", { refresh_token: refreshToken },
        { auth: false, keepalive: true, keepOnSignOut: true },
      ).catch(() => {}); // an offline sign-out must still clear locally — but only after a real attempt
    }
    this.applyTokens(undefined);
  }

  // --- Activation & entitlements (009) -------------------------------------
  /**
   * Bind this device. `licenseKey` is optional — when omitted the server resolves
   * the customer's tenant license (no client-held placeholder key).
   */
  async activate(deviceFingerprint: string, label?: string, licenseKey?: string): Promise<ActivationStatus> {
    return this.request("POST", "/api/activation/activate", {
      device_fingerprint: deviceFingerprint,
      label,
      ...(licenseKey ? { license_key: licenseKey } : {}),
    });
  }
  async activationStatus(): Promise<ActivationStatus> {
    return this.request("GET", "/api/activation/status");
  }
  async deactivateDevice(deviceId: string): Promise<void> {
    return this.request("POST", `/api/activation/devices/${deviceId}/deactivate`);
  }
  /** Claim the single production session for this device (spec 018 §0.7). Resolves with
   *  `{ok:true}` when granted; the cloud returns 409 `session_in_use` (a thrown error
   *  carrying status 409) when another device already holds the live session. */
  async claimSession(deviceFingerprint: string): Promise<{ ok: boolean; device_id: string }> {
    return this.request("POST", "/api/activation/session/claim",
      { device_fingerprint: deviceFingerprint });
  }
  async releaseSession(deviceFingerprint: string): Promise<{ ok: boolean; device_id: string }> {
    return this.request("POST", "/api/activation/session/release",
      { device_fingerprint: deviceFingerprint });
  }
  /** Customization options allowed for the current customer, gated by edition (spec 015). */
  async customizationCapabilities(): Promise<CustomizationCapabilities> {
    return this.request("GET", "/api/customization/capabilities");
  }
  // --- Per-workspace Settings (addendum §11) ---
  async getSettings(tenantId?: string): Promise<ResolvedWorkspaceSettings> {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
    return this.request("GET", `/api/settings${qs}`);
  }
  /** Update settings. `scope: "global"` targets the platform-wide OUTPUT defaults (the
   *  NULL-tenant row) — super-admin only; the server 403s otherwise. Omit for the caller's
   *  own tenant row (or `tenantId` for a super-admin targeting another tenant). */
  async updateSettings(
    input: UpdateSettingsInput,
    tenantId?: string,
    scope?: "global",
  ): Promise<ResolvedWorkspaceSettings> {
    const params = new URLSearchParams();
    if (tenantId) params.set("tenant_id", tenantId);
    if (scope) params.set("scope", scope);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.request("PUT", `/api/settings${qs}`, input);
  }
  /** Full FTP config incl password — for the desktop FTP exporter (owner's own credential). */
  async getFtpConfig(tenantId?: string): Promise<FtpConfig> {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
    return this.request("GET", `/api/settings/ftp-config${qs}`);
  }
  // --- Template-building options (resolved for the current customer; spec 016/§11) ---
  async templateOptions(): Promise<TemplateOptions> {
    return this.request("GET", "/api/template-options");
  }
  // --- Admin: entitlements editor ---
  async adminListEntitlements(): Promise<EntitlementsByTier> {
    return (await this.request<{ tiers: EntitlementsByTier }>("GET", "/api/admin/entitlements")).tiers;
  }
  async adminUpdateTierEntitlements(
    tier: string,
    features: Record<string, boolean | number | string | null>,
  ): Promise<void> {
    await this.request("PATCH", `/api/admin/entitlements/${tier}`, { features }, { raw: true });
  }
  // --- Platform white-label appearance ---
  /** PUBLIC — branding for the login screen + shell (app name, logo, accent). */
  async getAppearance(): Promise<Appearance> {
    return this.request("GET", "/api/appearance");
  }
  async updateAppearance(input: UpdateAppearanceInput): Promise<Appearance> {
    return this.request("PUT", "/api/admin/appearance", input);
  }
  /** Presign an R2/CDN target for the platform logo (super-admin). */
  async adminLogoUploadTarget(req: { ext: string }): Promise<ReferenceUploadTarget> {
    return this.request("POST", "/api/admin/appearance/logo-upload", req);
  }
  // --- Multi-brand profiles (spec 015 / B5) ---
  async listBrands(): Promise<BrandProfile[]> {
    return (await this.request<BrandProfile[]>("GET", "/api/brands")) ?? [];
  }
  async createBrand(input: CreateBrandInput): Promise<BrandProfile> {
    return this.request("POST", "/api/brands", input);
  }
  async updateBrand(id: string, input: UpdateBrandInput): Promise<BrandProfile> {
    return this.request("PATCH", `/api/brands/${id}`, input);
  }
  async deleteBrand(id: string): Promise<void> {
    return this.request("DELETE", `/api/brands/${id}`, undefined, { raw: true });
  }
  async entitlements(): Promise<Entitlements> {
    return this.request("GET", "/api/entitlements");
  }

  // --- Admin · customers (006) — role-scoped, tenant-confined --------------
  async listCustomers(): Promise<CustomerRecord[]> {
    return this.request("GET", "/api/admin/customers");
  }
  async getCustomer(id: string): Promise<CustomerRecord> {
    return this.request("GET", `/api/admin/customers/${id}`);
  }
  async createCustomer(input: CreateCustomerInput): Promise<CustomerRecord> {
    return this.request("POST", "/api/admin/customers", input);
  }
  /** Super-admin: toggle a customer's product mode / catalog edition (spec 011). */
  async updateCustomer(id: string, input: UpdateCustomerInput): Promise<CustomerRecord> {
    return this.request("PATCH", `/api/admin/customers/${id}`, input);
  }
  async blockCustomer(id: string): Promise<void> {
    return this.request("POST", `/api/admin/customers/${id}/block`);
  }
  async unblockCustomer(id: string): Promise<void> {
    return this.request("POST", `/api/admin/customers/${id}/unblock`);
  }
  /** Admin Activation tab: active devices for a customer (009). */
  async listCustomerDevices(id: string): Promise<DeviceInfo[]> {
    const r = await this.request<{ devices: DeviceInfo[] }>(
      "GET",
      `/api/admin/customers/${id}/devices`,
    );
    return r.devices;
  }

  // --- Admin · customer/license/device detail + control (Wave-4 console) ----
  /** Everything shown when an admin clicks a customer/store: license, devices, KPIs, live. */
  async customerDetail(id: string): Promise<CustomerDetail> {
    return this.request("GET", `/api/admin/customers/${id}/detail`);
  }
  /** Suspend the customer's activation(s) — desktop loses paid features immediately. */
  async revokeCustomerLicense(id: string): Promise<void> {
    return this.request("POST", `/api/admin/customers/${id}/license/revoke`);
  }
  async restoreCustomerLicense(id: string): Promise<void> {
    return this.request("POST", `/api/admin/customers/${id}/license/restore`);
  }
  /** Licenses in scope (tenant_admin → own tenant, super_admin → all). */
  async listLicenses(): Promise<LicenseSummary[]> {
    return this.request("GET", "/api/admin/licenses");
  }
  async licenseDetail(id: string): Promise<LicenseDetail> {
    return this.request("GET", `/api/admin/licenses/${id}`);
  }
  /** Every bound (active) device in scope, enriched with customer + license. */
  async listDevices(): Promise<BoundDevice[]> {
    const r = await this.request<{ devices: BoundDevice[] }>("GET", "/api/admin/devices");
    return r.devices ?? [];
  }
  /** Edit validity / seats / tier (super_admin). Returns the refreshed detail. */
  async updateLicense(id: string, input: UpdateLicenseInput): Promise<LicenseDetail> {
    return this.request("PATCH", `/api/admin/licenses/${id}`, input);
  }
  /**
   * Generate a new license (super_admin). The response carries the PLAINTEXT
   * `license_key` exactly once — show it to the issuer, then it's only ever masked.
   */
  async createLicense(input: CreateLicenseInput): Promise<CreatedLicense> {
    return this.request("POST", "/api/admin/licenses", input);
  }
  /** Delete a license + everything bound to it — activations & devices (super_admin). */
  async deleteLicense(id: string): Promise<void> {
    return this.request("DELETE", `/api/admin/licenses/${id}`);
  }
  /** Add a license to a customer (grant a seat). Returns the refreshed detail. */
  async assignLicense(id: string, customerId: string): Promise<LicenseDetail> {
    const input: AssignLicenseInput = { customer_id: customerId };
    return this.request("POST", `/api/admin/licenses/${id}/assign`, input);
  }
  /** Remove a license from a customer (free their seat). Returns refreshed detail. */
  async unassignLicense(id: string, customerId: string): Promise<LicenseDetail> {
    const input: AssignLicenseInput = { customer_id: customerId };
    return this.request("POST", `/api/admin/licenses/${id}/unassign`, input);
  }
  /** Admin deactivation of any in-scope device — frees a seat (009). */
  async adminDeactivateDevice(deviceId: string): Promise<void> {
    return this.request("POST", `/api/admin/devices/${deviceId}/deactivate`);
  }

  /**
   * Cloud liveness (shallow) or READINESS (`deep`) — public, no auth.
   *
   * The shallow `/health` is DB-free **by design** (`cloud/app/main.py:542-560`: the container
   * must report healthy before the background migration runs), so it always answers
   * `db: "unchecked"` — a value that means "not measured", never "broken". `?deep=1` is the
   * probe that actually opens a session and runs `SELECT 1`, and since PR #35 it also reports
   * `seed`: `"pending" | "ok" | "incomplete"`.
   *
   * A deep probe answers **503** when the database errored or the startup seed is incomplete
   * — and the 503 **body is the report**. That is the probe working, not the API being
   * unreachable, so the deep path returns it. Anything else (a transport failure, a 503 with
   * no report in it) still throws, and the shallow path is untouched for its existing callers.
   */
  async health(opts?: { deep?: boolean }): Promise<HealthReport> {
    if (!opts?.deep) return this.request("GET", "/health");
    try {
      return await this.request<HealthReport>("GET", "/health?deep=1");
    } catch (e) {
      if (e instanceof HttpError && e.status === 503 && isHealthReport(e.body)) return e.body;
      throw e;
    }
  }

  // --- Admin · AI provider settings + usage (007) — super_admin ------------
  async getAiSettings(): Promise<AiSettings> {
    return this.request("GET", "/api/admin/ai/settings");
  }
  async setAiSettings(input: AiSettingsInput): Promise<AiSettings> {
    return this.request("PUT", "/api/admin/ai/settings", input);
  }
  async aiUsage(groupBy: "provider" | "tenant" = "provider"): Promise<AiUsage> {
    return this.request("GET", `/api/admin/ai/usage?group_by=${groupBy}`);
  }
  /**
   * WS-HF — the Higgsfield usage history the admin Usage page reads when there is no MCP
   * connection: KPIs, the per-model share, and one page of recorded generations.
   *
   * Every parameter is optional and only SET ones are sent, so the server keeps its own
   * defaults for a window this client did not ask about.
   */
  async higgsfieldUsage(query: HiggsfieldUsageQuery = {}): Promise<HiggsfieldUsage> {
    const qs = new URLSearchParams();
    if (query.from) qs.set("from", query.from);
    if (query.to) qs.set("to", query.to);
    if (query.page !== undefined) qs.set("page", String(query.page));
    if (query.size !== undefined) qs.set("size", String(query.size));
    if (query.model) qs.set("model", query.model);
    if (query.status) qs.set("status", query.status);
    if (query.txCursor !== undefined) qs.set("tx_cursor", String(query.txCursor));
    if (query.txSize !== undefined) qs.set("tx_size", String(query.txSize));
    const q = qs.toString();
    return this.request("GET", `/api/admin/ai/usage/higgsfield${q ? `?${q}` : ""}`);
  }

  // --- WS-HF shared login: the company Higgsfield subscription — super_admin ----------
  /** Start the company sign-in. Answers the page to open; the browser comes back to the
   *  CLOUD's callback, never to this client. */
  async higgsfieldSubscriptionLogin(): Promise<HiggsfieldSubscriptionLogin> {
    return this.request("POST", "/api/admin/ai/higgsfield/mcp/login");
  }
  /** Connected, plan, balance, who connected it and when. */
  async higgsfieldSubscriptionStatus(): Promise<HiggsfieldSubscriptionStatus> {
    return this.request("GET", "/api/admin/ai/higgsfield/mcp/status");
  }
  /** Forget the company account. Answers the (now disconnected) status. */
  async higgsfieldSubscriptionDisconnect(): Promise<HiggsfieldSubscriptionStatus> {
    return this.request("POST", "/api/admin/ai/higgsfield/mcp/disconnect");
  }
  /** Re-attach held case stages from an earlier, unidentified connection to the account
   *  connected now. Only for when the admin knows it is the SAME account. */
  async higgsfieldSubscriptionReattach(): Promise<{ reattached: number }> {
    return this.request("POST", "/api/admin/ai/higgsfield/mcp/reattach");
  }
  /** Recent brokered AI calls — full prompt sent + response metadata (super-admin). */
  async aiCalls(limit = 100): Promise<AiCallLogEntry[]> {
    return (await this.request<AiCallLogEntry[]>("GET", `/api/admin/ai/calls?limit=${limit}`)) ?? [];
  }
  /** One brokered AI call in full — same shape as a list row, incl. cost_usd +
   *  owner_email (super-admin; 404 when unknown). */
  async aiCallDetail(id: string): Promise<AiCallLogEntry> {
    return this.request("GET", `/api/admin/ai/calls/${encodeURIComponent(id)}`);
  }

  // --- Admin · hosting settings (FR-008) — super_admin ---------------------
  async getHostingSettings(): Promise<HostingSettings> {
    return this.request("GET", "/api/admin/hosting/settings");
  }
  async setHostingSettings(input: HostingSettingsInput): Promise<HostingSettings> {
    return this.request("PUT", "/api/admin/hosting/settings", input);
  }

  // --- Hosting · signed-upload broker (FR-008) — pixels never hit the API ---
  /** Broker an upload target + public URL for one output descriptor. */
  async signedUpload(descriptor: OutputDescriptor): Promise<SignedUpload> {
    return this.request("POST", "/api/hosting/signed-upload", descriptor);
  }

  // --- AI relay (007) — relay only, no server render -----------------------
  /**
   * Image-to-image generation: relay a reference image + a product image + a
   * text prompt (instructions) to the active provider and get the generated image
   * back as a base64 data-URI, byte-for-byte (no server processing). The prompt is
   * sandboxed server-side (the "generate" guardrail stage); a real provider call
   * requires an active activation (403) and a configured key (503 otherwise). Maps
   * snake_case `media_type` → `mediaType` for the client.
   */
  async generate(
    reference: Uint8Array | Uint8Array[],
    product: Uint8Array | Uint8Array[],
    prompt: string,
    // Spec 017 (US2): a studio-plan customer sends `view` + selected `optionChoiceIds`
    // (no free prompt); the server composes the trusted harness. `prompt` stays for the
    // admin/author path. `brandId` must reference an APPROVED brand.
    // `product` / `caseStyle` fill the generate template's own placeholders — the
    // catalog words for WHAT is being drawn. Without them the prompt reaches the
    // provider with three blank labels ("Product:", "Case style:", "View:").
    opts?: {
      view?: string; optionChoiceIds?: string[]; brandId?: string;
      product?: string; caseStyle?: string; productType?: string;
    },
  ): Promise<GenerateResult> {
    // Accept one OR many of each — all uploaded images are sent in a single call
    // (multi-image generation). A lone Uint8Array stays a 1-element request.
    const refs = Array.isArray(reference) ? reference : [reference];
    const prods = Array.isArray(product) ? product : [product];
    const fd = new FormData();
    for (const r of refs) fd.append("reference_image", new Blob([r as BlobPart]), "reference_image");
    for (const p of prods) fd.append("product_image", new Blob([p as BlobPart]), "product_image");
    fd.append("prompt", prompt);
    if (opts?.view) fd.append("view", opts.view);
    if (opts?.product) fd.append("product", opts.product);
    if (opts?.productType) fd.append("product_type", opts.productType);
    if (opts?.caseStyle) fd.append("case_style", opts.caseStyle);
    if (opts?.brandId) fd.append("brand_id", opts.brandId);
    for (const id of opts?.optionChoiceIds ?? []) fd.append("option_choice_ids", id);
    const r = await this.request<{ model: string; media_type: string; image: string }>(
      "POST",
      "/api/ai/generate",
      undefined,
      { multipart: fd },
    );
    return { model: r.model, mediaType: r.media_type, image: r.image };
  }

  // --- Generation queue (P1 — server-side, non-blocking) ------------------
  /** Enqueue ONE generation job per selected view (the design is the input). Returns
   *  immediately with the queued jobs; a background worker runs the real generation. */
  async enqueueGeneration(
    design: Uint8Array,
    views: string[],
    opts?: {
      optionChoiceIds?: string[];
      templateId?: string;
      brandId?: string;
      mime?: string;
      details?: {
        productType?: string; brand?: string; model?: string; name?: string;
        phoneModelId?: string; caseStyleId?: string; baseType?: string;
      };
      /** Case references 2..5, sent after `design` as further `design_image` parts. */
      moreCaseImages?: { bytes: Uint8Array; mime?: string }[];
      /** The optional PRODUCT reference — a photo of the real phone, sent after the case. */
      productImage?: { bytes: Uint8Array; mime?: string };
      /**
       * WS-HF: who RUNS this job. Omitted (the default) means the cloud worker generates it
       * with the platform's provider keys, exactly as before. `"device"` means the desktop
       * app will generate it on the operator's own Higgsfield subscription and POST the
       * result back — the cloud then only holds the record and the input.
       *
       * Sent only when set, so a cloud that does not know the field never sees it.
       */
      executor?: "cloud" | "device";
      /** The caller shows the clear-image review: a two-pass job then waits for a person after
       *  pass 1. Without it the cloud runs both passes straight on, as older clients expect. */
      clearReview?: boolean;
      /** Sent only when set: a cloud given none prints the whole back, as before the choice. */
      printArea?: GenerationPrintArea;
      /** Sent only for `"mockup"`, so a cloud without the choice never sees the field. */
      output?: GenerationOutput;
      /** 32 lowercase hex, one per Generate press: the views enqueued one by one are one run. */
      runId?: string;
    },
  ): Promise<GenerationJobView[]> {
    const fd = new FormData();
    fd.append("design_image", new Blob([design as BlobPart], { type: opts?.mime ?? "image/png" }), "design");
    for (const { bytes, mime } of opts?.moreCaseImages ?? []) {
      fd.append("design_image", new Blob([bytes as BlobPart], { type: mime ?? "image/png" }), "design");
    }
    if (opts?.productImage) {
      const { bytes, mime } = opts.productImage;
      fd.append("product_image", new Blob([bytes as BlobPart], { type: mime ?? "image/png" }), "product");
    }
    for (const v of views) fd.append("view", v);
    for (const id of opts?.optionChoiceIds ?? []) fd.append("option_choice_ids", id);
    if (opts?.templateId) fd.append("template_id", opts.templateId);
    if (opts?.brandId) fd.append("brand_id", opts.brandId);
    if (opts?.details?.productType) fd.append("product_type", opts.details.productType);
    if (opts?.details?.brand) fd.append("brand", opts.details.brand);
    if (opts?.details?.model) fd.append("model", opts.details.model);
    if (opts?.details?.name) fd.append("name", opts.details.name);
    if (opts?.details?.phoneModelId) fd.append("phone_model_id", opts.details.phoneModelId);
    if (opts?.details?.caseStyleId) fd.append("case_style_id", opts.details.caseStyleId);
    if (opts?.details?.baseType) fd.append("base_type", opts.details.baseType);
    if (opts?.executor) fd.append("executor", opts.executor);
    if (opts?.clearReview) fd.append("clear_review", "true");
    if (opts?.printArea) fd.append("print_area", opts.printArea);
    if (opts?.output === "mockup") fd.append("output", "mockup");
    if (opts?.runId) fd.append("run_id", opts.runId);
    const r = await this.request<{ count: number; jobs: WireGenJob[] }>(
      "POST", "/api/generation-jobs", undefined, { multipart: fd, keepOnSignOut: opts?.executor === "device" },
    );
    return (r.jobs ?? []).map(wireGenJob);
  }

  /** The current OWNER's generation jobs, newest first, + a status summary of all of them.
   *  `before` is the last row's `createdAt`, for the next page. */
  async listGenerationJobs(opts?: { limit?: number; before?: string; output?: GenerationOutput }): Promise<{
    jobs: GenerationJobView[];
    summary: GenerationJobsSummary;
  }> {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.before) qs.set("before", opts.before);
    if (opts?.output) qs.set("output", opts.output);
    const q = qs.toString();
    const r = await this.request<{
      jobs: WireGenJob[];
      summary: { total: number; by_status: Record<string, number> };
    }>("GET", `/api/generation-jobs${q ? `?${q}` : ""}`);
    return { jobs: (r.jobs ?? []).map(wireGenJob), summary: wireJobsSummary(r.summary) };
  }

  /** The current OWNER's newest runs (a run is one Generate press; a job without one is its own
   *  run), every job of each, so a run is never split across pages. `before` is the previous
   *  page's `nextBefore`. */
  async listGenerationRuns(opts?: { limit?: number; before?: string; output?: GenerationOutput }): Promise<GenerationRunsPage> {
    const qs = new URLSearchParams({ group: "run" });
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.before) qs.set("before", opts.before);
    if (opts?.output) qs.set("output", opts.output);
    const r = await this.request<{
      jobs: WireGenJob[];
      summary: { total: number; by_status: Record<string, number> };
      next_before?: string | null;
    }>("GET", `/api/generation-jobs?${qs}`);
    return {
      jobs: (r.jobs ?? []).map(wireGenJob),
      summary: wireJobsSummary(r.summary),
      nextBefore: r.next_before ?? null,
    };
  }

  /** Record how far the owner took a result. Forward only; `published` names the template
   *  the publish created. */
  async recordGenerationProgress(
    id: string, step: GenerationHandoffStep, templateId?: string,
  ): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>(
      "POST", `/api/generation-jobs/${id}/progress`, { step, ...(templateId ? { templateId } : {}) },
    ));
  }

  /** A person's decision on a two-pass job's clear image: `approve` runs pass 2 on it,
   *  `regenerate` pays for a new pass 1. `clearRef` names the image the person saw; 409 when
   *  the job moved or the image changed. */
  async reviewClearImage(
    id: string, decision: "approve" | "regenerate", clearRef: string,
  ): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>(
      "POST", `/api/generation-jobs/${id}/clear-review`, { decision, clearRef },
    ));
  }

  /** Stop an ongoing job (queued, processing or waiting in clear review). 409 when it already
   *  finished; a paid request already out is still charged. */
  async cancelGenerationJob(id: string): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>("POST", `/api/generation-jobs/${id}/cancel`));
  }

  /** Run the print guide step again on a settled job's stored first image, at the settings saved
   *  now. 409 when the job has no first image, is still running, or moved. */
  async retryMagentaGenerationJob(id: string): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>("POST", `/api/generation-jobs/${id}/retry-magenta`));
  }

  /** Relabel a settled mockup-only job's Details; the image never changes. 409 `not_editable`
   *  for any other job; the owner, their tenant admin or a super-admin. */
  async updateGenerationJob(id: string, details: GenerationDetailsEdit): Promise<GenerationJobView> {
    const wire: Record<string, string | null | undefined> = {
      name: details.name, product_type: details.productType, brand: details.brand, model: details.model,
      phone_model_id: details.phoneModelId, case_style_id: details.caseStyleId, base_type: details.baseType,
    };
    const body = Object.fromEntries(Object.entries(wire).filter(([, v]) => v !== undefined));
    return wireGenJob(await this.request<WireGenJob>("PATCH", `/api/generation-jobs/${id}`, body));
  }

  /** A new job of the same output, in the same run, from a settled job's stored inputs (owner
   *  only, one paid image). A print job comes back with its clear-image review on. The old job and
   *  its image stay. 409 `not_regenerable` for a job still running, held, or made on a device. */
  async regenerateGenerationJob(id: string): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>("POST", `/api/generation-jobs/${id}/regenerate`));
  }

  /** Remove a job in any status from the owner's list; an ongoing one is cancelled first. The
   *  template made from it stays. */
  async removeGenerationJob(id: string): Promise<void> {
    await this.request("DELETE", `/api/generation-jobs/${id}`);
  }

  /** One job's current status + result ref (owner-only).
   *
   *  For a `device` job this is also the hand-off: the response carries `inputUrl`,
   *  `productUrl`, `viewUrl` and `promptText` — exactly what the cloud worker would have sent —
   *  so a device run reproduces the cloud run rather than inventing its own request. */
  /** `inputs` asks for the uploaded references' links too, which a finished cloud job leaves out. */
  async getGenerationJob(id: string, opts?: { keepOnSignOut?: boolean; inputs?: boolean }): Promise<GenerationJobView> {
    const query = opts?.inputs ? "?inputs=1" : "";
    return wireGenJob(await this.request<WireGenJob>("GET", `/api/generation-jobs/${id}${query}`, undefined, opts));
  }

  /** Post the finished image of a DEVICE run back to the cloud.
   *
   *  Accepted ONCE, by the job's owner, while the job is still open — a retry after a
   *  flaky response answers 409 rather than storing a second image and billing a second
   *  time. `credits` is what the provider actually charged this run; omit it when the
   *  device could not read a figure, because a fabricated 0 reads as a free call on the
   *  usage page. */
  async submitDeviceResult(
    jobId: string,
    image: Uint8Array,
    opts?: { provider?: string; model?: string; credits?: number; mediaType?: string },
  ): Promise<GenerationJobView> {
    const fd = new FormData();
    fd.append("image", new Blob([image as BlobPart]), "result.png");
    if (opts?.provider) fd.append("provider", opts.provider);
    if (opts?.model) fd.append("model", opts.model);
    if (opts?.credits !== undefined) fd.append("credits", String(opts.credits));
    if (opts?.mediaType) fd.append("media_type", opts.mediaType);
    return wireGenJob(await this.request<WireGenJob>(
      "POST", `/api/generation-jobs/${jobId}/result`, undefined, { multipart: fd, keepOnSignOut: true },
    ));
  }

  /** Report a DEVICE run that failed (provider error, moderation, cancellation).
   *  Writes no usage record — a failed or moderated generation is not charged. */
  async submitDeviceFailure(jobId: string, error: string): Promise<GenerationJobView> {
    return wireGenJob(await this.request<WireGenJob>(
      "POST", `/api/generation-jobs/${jobId}/fail`, { error }, { keepOnSignOut: true },
    ));
  }

  /** Admin: ALL generation jobs WITH owner email; filter by status / owner. */
  async adminListGenerationJobs(
    opts?: { status?: string; customerId?: string; output?: GenerationOutput },
  ): Promise<GenerationJobView[]> {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.customerId) qs.set("customer_id", opts.customerId);
    if (opts?.output) qs.set("output", opts.output);
    const q = qs.toString();
    const rows = await this.request<WireGenJob[]>(
      "GET", `/api/admin/generation-jobs${q ? `?${q}` : ""}`,
    );
    return (rows ?? []).map(wireGenJob);
  }

  // --- Two-stage case jobs (WS-HF) ----------------------------------------
  /** `/api/case-jobs` — the owner's Higgsfield canvas workflow.
   *
   *  Grouped on one object rather than spread across seven `caseJobX` methods
   *  because they are a single state machine: a caller that has one of these almost
   *  always needs the next one, and the grouping keeps the transitions readable at
   *  the call site (`caseJobs.approve` / `caseJobs.retry`).
   *
   *  There is deliberately NO `colours` method. Applying hex colours and artwork,
   *  building the print and alpha masks and writing transparent exports all run in
   *  the engine ON THE DEVICE, reusing the approved base — extra colours cost no
   *  generation, and routing them through the cloud would turn a free local
   *  operation into a round trip. */
  readonly caseJobs = {
    /** Start a build: the three photographs plus the product rule. Stage 1 runs on
     *  the worker (cloud) or on this machine (device). */
    create: async (input: {
      phone: Uint8Array;
      case: Uint8Array;
      crop: Uint8Array;
      /** Each prompt's product rule. All optional — a missing or blank one takes
       *  the canvas's own sentence, so an empty object renders the canvas text. */
      caseRules?: Partial<Record<CaseRuleKey, string>>;
      executor?: GenerationJobExecutor;
      templateId?: string;
      models?: Partial<Record<CaseJobStage, string>>;
      mime?: string;
      /** The real phone every stage prompt names — required by the cloud. */
      brand?: string;
      model?: string;
      productType?: string;
      /** An ADMIN's per-job product height (70–98), or `null` for no height restriction.
       *  Omitted → the company default. The cloud ignores it for a customer. */
      productHeightPct?: number | null;
    }): Promise<CaseJobView> => {
      const fd = new FormData();
      const type = input.mime ?? "image/png";
      fd.append("phone", new Blob([input.phone as BlobPart], { type }), "phone");
      fd.append("case", new Blob([input.case as BlobPart], { type }), "case");
      fd.append("crop", new Blob([input.crop as BlobPart], { type }), "crop");
      for (const key of ["clear", "magenta", "repair"] as const) {
        const rule = input.caseRules?.[key];
        if (rule) fd.append(`case_rules_${key}`, rule);
      }
      if (input.executor) fd.append("executor", input.executor);
      if (input.templateId) fd.append("template_id", input.templateId);
      if (input.models?.clear) fd.append("model_clear", input.models.clear);
      if (input.models?.magenta) fd.append("model_magenta", input.models.magenta);
      if (input.brand) fd.append("brand", input.brand);
      if (input.model) fd.append("model", input.model);
      if (input.productType) fd.append("product_type", input.productType);
      if (input.productHeightPct !== undefined) {
        fd.append("product_height_pct", input.productHeightPct === null ? "none" : String(input.productHeightPct));
      }
      return this.request<CaseJobView>("POST", "/api/case-jobs", undefined, { multipart: fd });
    },

    /** One job: its stage, its previews, what it has cost so far, and the prompt the
     *  current stage will send. Owner only — anyone else gets a 404. */
    get: (id: string): Promise<CaseJobView> =>
      this.request<CaseJobView>("GET", `/api/case-jobs/${id}`),

    /** Accept the stage under review. Idempotent, so a double-clicked button or a
     *  retried request answers with the job as it stands rather than an error. */
    approve: (id: string): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/approve`),

    /** Buy ONE more attempt at the stage under review; a second answers 409
     *  `retry_limit_reached`. `repair` swaps in the archived camera-opening
     *  correction and sends the rejected result as an extra reference.
     *
     *  `repair` is always sent, never omitted: this is the one call that authorises
     *  a second charge, so the request says explicitly which of the two prompts it
     *  is paying for instead of leaving the server to assume a default. */
    retry: (id: string, opts?: { repair?: boolean }): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/retry`, {
        repair: opts?.repair ?? false,
      }),

    /** Report that the desktop's automatic magenta key failed on the image under review
     *  (`coverage_check_failed: …` / `autokey_not_found: …`). The stage then waits for its
     *  one retry instead of handing the same image over again. */
    reportCheck: (id: string, stage: CaseJobStage, reason: string): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/check`, { stage, reason }),

    /** Post the finished image of a DEVICE-run stage.
     *
     *  Accepted ONCE, by the owner, while the stage is open — a retry after a flaky
     *  response answers 409 rather than storing a second image and billing again.
     *  Omit `credits` when the device could not read a figure: a fabricated 0 reads
     *  as a free generation on the usage page.
     *
     *  `attemptToken` is the job's `deviceAttemptToken` as read before the run. It
     *  names the attempt this image finishes: without it, or with a retired one, the
     *  server answers 409 `stale_attempt` (refresh the job and run what it says). */
    stageResult: (
      id: string,
      image: Uint8Array,
      opts?: {
        model?: string; credits?: number; mediaType?: string; requestId?: string;
        attemptToken?: string;
      },
    ): Promise<CaseJobView> => {
      const fd = new FormData();
      fd.append("image", new Blob([image as BlobPart]), "stage.png");
      if (opts?.model) fd.append("model", opts.model);
      if (opts?.credits !== undefined) fd.append("credits", String(opts.credits));
      if (opts?.mediaType) fd.append("media_type", opts.mediaType);
      // Required on a HELD stage: the vendor request the late image belongs to. It
      // must be the one the stage holds, or the server answers 409.
      if (opts?.requestId) fd.append("request_id", opts.requestId);
      if (opts?.attemptToken) fd.append("attempt_token", opts.attemptToken);
      return this.request<CaseJobView>(
        "POST", `/api/case-jobs/${id}/stage-result`, undefined, { multipart: fd },
      );
    },

    /** Report a DEVICE-run stage that failed. Writes no usage record — a failed or
     *  moderated generation is not charged. `attemptToken` as for `stageResult`. */
    stageFail: (id: string, error: string, attemptToken?: string): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/stage-fail`,
        attemptToken ? { error, attemptToken } : { error }),

    /** Report a DEVICE-run stage the vendor ACCEPTED but whose outcome was lost (a
     *  poll timed out, the app quit mid-run). The stage goes to `*_held`: retryable,
     *  never terminal, nothing billed. Use this instead of `stageFail` whenever the
     *  vendor has issued a job id — the image may exist and may be charged, and
     *  `requestId` is what the owner checks before paying again. `attemptToken` as
     *  for `stageResult`. */
    stageHeld: (
      id: string, requestId: string, note?: string, attemptToken?: string,
    ): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/stage-held`, {
        requestId,
        ...(note ? { note } : {}),
        ...(attemptToken ? { attemptToken } : {}),
      }),

    /** Stop the job. A stage already in flight is not cancelled at the provider —
     *  its credits are spent either way — but nothing further is submitted. */
    cancel: (id: string): Promise<CaseJobView> =>
      this.request<CaseJobView>("POST", `/api/case-jobs/${id}/cancel`),
  };

  // --- Unified options (spec 017 Feature A) -------------------------------
  /** Customer: the resolved dropdown options (NO harness text). */
  async getResolvedOptions(): Promise<ResolvedOptions> {
    return (await this.request<ResolvedOptions>("GET", "/api/options/resolved"))
      ?? { groups: [] };
  }
  /** Super-admin: all option groups + choices (incl. harness, for authoring). */
  async adminListOptions(): Promise<{ groups: OptionGroup[] }> {
    return (await this.request<{ groups: OptionGroup[] }>("GET", "/api/admin/options"))
      ?? { groups: [] };
  }
  async adminCreateOptionGroup(input: {
    key: string; label: string; kind?: string; select?: string; order?: number;
    tenant_id?: string | null;
  }): Promise<OptionGroup> {
    return this.request("POST", "/api/admin/options/groups", input);
  }
  async adminUpdateOptionGroup(id: string, patch: Partial<OptionGroup>): Promise<OptionGroup> {
    return this.request("PATCH", `/api/admin/options/groups/${id}`, patch);
  }
  async adminCreateOptionChoice(input: {
    group_id: string; value: string; label: string; reference_ref?: string | null;
    harness_prompt?: string | null; required?: boolean; order?: number;
  }): Promise<OptionChoice> {
    return this.request("POST", "/api/admin/options/choices", input);
  }
  async adminUpdateOptionChoice(id: string, patch: Partial<OptionChoice>): Promise<OptionChoice> {
    return this.request("PATCH", `/api/admin/options/choices/${id}`, patch);
  }
  async adminSetOptionAssignment(input: {
    scope_type: string; scope_key: string; group_id: string;
    allowed_choice_ids: string[]; visible: boolean;
  }): Promise<OptionAssignment> {
    return this.request("PUT", "/api/admin/options/assignments", input);
  }
  /** Remove a scope's override so the group falls back to the broader scope (customer >
   *  edition > global). Setting an "allow everything" assignment is NOT the same thing — the
   *  narrow row still shadows the broader one. 404 when there is no such override. */
  async adminDeleteOptionAssignment(
    scopeType: string, scopeKey: string, groupId: string,
  ): Promise<void> {
    return this.request("DELETE", `/api/admin/options/assignments/${scopeType}/${scopeKey}/${groupId}`);
  }
  async adminGetOptionAssignments(
    scopeType: string, scopeKey: string,
  ): Promise<{ assignments: OptionAssignment[] }> {
    return (await this.request<{ assignments: OptionAssignment[] }>(
      "GET", `/api/admin/options/assignments/${scopeType}/${scopeKey}`,
    )) ?? { assignments: [] };
  }
  async adminOptionReferenceUpload(
    req: { ext: string; group_id: string; value: string },
  ): Promise<ReferenceUploadTarget> {
    return this.request("POST", "/api/admin/options/reference-upload", req);
  }

  // --- Brand lifecycle (spec 017 Feature D) -------------------------------
  /** Customer: submit a brand for admin approval (draft|rejected → pending). */
  async submitBrand(id: string): Promise<BrandProfile> {
    return this.request("POST", `/api/brands/${id}/submit`);
  }
  /** Customer (brand owner): presign an R2/CDN target for the brand's logo (item 9).
   *  PUT the bytes to `upload_url`, then `updateBrand(id, { logo_ref: public_url })`. */
  async brandLogoUpload(id: string, ext: string): Promise<ReferenceUploadTarget> {
    return this.request("POST", `/api/brands/${id}/logo-upload`, { ext });
  }
  /**
   * Admin: the cross-customer brand list. Pass a `status` to filter the queue
   * (e.g. "pending"); pass "all" (or omit) to return every status — the P4
   * management table reads them all and filters client-side.
   */
  async adminListBrands(status: BrandStatus | "all" = "all"): Promise<BrandProfile[]> {
    const qs = status === "all" ? "" : `?status=${encodeURIComponent(status)}`;
    return (await this.request<BrandProfile[]>("GET", `/api/admin/brands${qs}`)) ?? [];
  }
  async approveBrand(id: string): Promise<BrandProfile> {
    return this.request("POST", `/api/admin/brands/${id}/approve`);
  }
  /** Admin: reject a brand → `rejected`. Optional `reason` (item 9) is stored and
   *  surfaced back to the owner (returned in `BrandProfile.rejection_reason`). */
  async rejectBrand(id: string, reason?: string): Promise<BrandProfile> {
    return this.request("POST", `/api/admin/brands/${id}/reject`, { reason: reason ?? null });
  }
  /** Admin: revoke an approved brand → back to `draft` (the locked decision). */
  async revokeBrand(id: string): Promise<BrandProfile> {
    return this.request("POST", `/api/admin/brands/${id}/revoke`);
  }
  /** Admin: delete a brand outright (super_admin any tenant; tenant_admin own). */
  async adminDeleteBrand(id: string): Promise<void> {
    return this.request("DELETE", `/api/admin/brands/${id}`, undefined, { raw: true });
  }
  /** Admin: create a brand FOR a chosen customer; born `approved` (reassignable). */
  async adminCreateBrand(input: {
    customerId: string; name: string; colorPrimary?: string; colorSecondary?: string;
  }): Promise<BrandProfile> {
    return this.request("POST", "/api/admin/brands", {
      customer_id: input.customerId,
      name: input.name,
      color_primary: input.colorPrimary,
      color_secondary: input.colorSecondary,
    });
  }
  /** Admin: reassign a brand's owner (and transitively its tenant) to another customer. */
  async adminAssignBrand(id: string, customerId: string): Promise<BrandProfile> {
    return this.request("PATCH", `/api/admin/brands/${id}/assign`, { customer_id: customerId });
  }

  // --- Catalog (003) --------------------------------------------------------
  async listTemplates(query?: {
    phone_model?: string; case_style?: string; status?: string;
    // Spec 017 Feature C (US4) — Brand-plan browse/search filters.
    brand?: string; category_type?: string; category?: string; product_category?: string;
    /** Server-side free text over name/code/type/brand/style (WS-C). */
    q?: string;
  }): Promise<ProductTemplate[]> {
    const qs = query ? `?${new URLSearchParams(query as Record<string, string>)}` : "";
    // WS-B B-4: tolerate WS-C's `TemplatePage` envelope as well as the bare array this
    // route returns by default. No `limit`/`cursor` is sent — this is not the pagination
    // entry point (`listTemplatesPage` below is); the tolerance exists purely so the
    // envelope cannot break the client whichever order the two waves land in.
    const body = await this.request<WireTemplateSummary[] | WireTemplatePage>(
      "GET", `/api/templates${qs}`);
    return templateRows(body).map(wireSummaryToTemplate);
  }
  /** Permission-based Sync tab (spec 013): templates the current customer may sync,
   *  chosen server-side by their edition (Studio → own private; Brand →
   *  shared platform library). */
  async listSyncable(): Promise<ProductTemplate[]> {
    const body = await this.request<WireTemplateSummary[] | WireTemplatePage>(
      "GET", "/api/templates/sync");
    return templateRows(body).map(wireSummaryToTemplate);
  }
  /**
   * One keyset page of the browse listing. Sends `limit` unconditionally, so the server
   * always answers with the envelope — the bare-array reply is reserved for callers that
   * ask for no page at all. Pass the previous page's `nextCursor` to continue.
   */
  async listTemplatesPage(query?: {
    phone_model?: string; case_style?: string; status?: string;
    brand?: string; category_type?: string; category?: string; product_category?: string;
    q?: string; limit?: number; cursor?: string;
  }): Promise<TemplatePage> {
    const params: Record<string, string> = { limit: String(query?.limit ?? 200) };
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== "") params[k] = String(v);
    }
    const page = await this.request<WireTemplatePage>(
      "GET", `/api/templates?${new URLSearchParams(params)}`);
    return {
      items: templateRows(page).map(wireSummaryToTemplate),
      nextCursor: page?.next_cursor ?? null,
      limit: page?.limit ?? Number(params.limit),
    };
  }
  /** One keyset page of the Sync listing (same contract as `listTemplatesPage`). */
  async listSyncablePage(query?: { q?: string; limit?: number; cursor?: string }): Promise<TemplatePage> {
    const params: Record<string, string> = { limit: String(query?.limit ?? 200) };
    if (query?.cursor) params.cursor = query.cursor;
    if (query?.q) params.q = query.q;
    const page = await this.request<WireTemplatePage>(
      "GET", `/api/templates/sync?${new URLSearchParams(params)}`);
    return {
      items: templateRows(page).map(wireSummaryToTemplate),
      nextCursor: page?.next_cursor ?? null,
      limit: page?.limit ?? Number(params.limit),
    };
  }
  /** New-model requests for the current customer's tenant (spec 013). */
  async listModelRequests(): Promise<ModelRequest[]> {
    return (await this.request<ModelRequest[]>("GET", "/api/model-requests")) ?? [];
  }
  /** Request a phone model that isn't available yet (spec 013). */
  async createModelRequest(input: { brand: string; model: string }): Promise<ModelRequest> {
    return this.request("POST", "/api/model-requests", input);
  }
  async getTemplate(id: string): Promise<ProductTemplate> {
    const t = await this.request<WireTemplateFull>("GET", `/api/templates/${id}`);
    return wireFullToTemplate(t);
  }
  /** Auto view-detection (spec 016): rank the tenant's templates by the engine's
   *  perceptual image hash (and/or phone model). The client computes `image_hash` via
   *  `EngineBinding.perceptualHash`. */
  async matchTemplates(input: {
    phone_model_id?: string;
    image_hash?: string;
    max_distance?: number;
    image_embedding?: number[];
    min_similarity?: number;
  }): Promise<TemplateMatch[]> {
    return (await this.request<TemplateMatch[]>("POST", "/api/templates/match", input)) ?? [];
  }
  /** Store a template's reference perceptual hash so it becomes image-matchable (spec 016). */
  async setTemplateRefHash(id: string, refHash: string): Promise<void> {
    await this.request("PATCH", `/api/templates/${id}`, { ref_hash: refHash }, { raw: true });
  }
  /** Store a template's neural embedding (the higher-fidelity match; spec 016). */
  async setTemplateRefEmbedding(id: string, embedding: number[]): Promise<void> {
    await this.request("PATCH", `/api/templates/${id}`, { ref_embedding: embedding }, { raw: true });
  }
  async listVersions(templateId: string): Promise<TemplateVersion[]> {
    const versions = await this.request<WireVersionOut[]>("GET", `/api/templates/${templateId}/versions`);
    return (versions ?? []).map(wireVersionToView);
  }
  /**
   * Create a brand-new product template (cloud `POST /api/templates`,
   * tenant_confined). Returns the template + its initial Draft version (v1).
   */
  async createTemplate(input: CreateTemplateInput): Promise<CreatedTemplate> {
    const r = await this.request<{ template_id: string; version_id: string }>(
      "POST",
      "/api/templates",
      {
        name: input.name,
        product_type: input.productType,
        phone_model_id: input.phoneModelId,
        case_style_id: input.caseStyleId,
        base_type: input.baseType,
        code: input.code,
      },
    );
    return { templateId: r.template_id, versionId: r.version_id };
  }
  /**
   * Edit a template's DETAILS (spec 022 / #4): product type, model, style, base type, code.
   * Metadata-only — never masks — so it's allowed on a locked/Active template.
   *
   * `undefined` (or an omitted key) = leave the field alone. An explicit `null` = CLEAR it:
   * the cloud stores NULL for model / style / base type, re-issues a server SKU for `code`,
   * and 422s by name for the fields whose column is NOT NULL (`productType`, `name`).
   * Before WS-C there was no clear verb on the wire at all, so an emptied field silently
   * snapped back on the next refetch. Returns the refreshed full template.
   */
  async updateTemplateDetails(
    templateId: string,
    input: {
      name?: string;
      productType?: string;
      phoneModelId?: string | null;
      caseStyleId?: string | null;
      baseType?: string | null;
      code?: string | null;
    },
  ): Promise<ProductTemplate> {
    const t = await this.request<WireTemplateFull>("PATCH", `/api/templates/${templateId}`, {
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.productType !== undefined ? { product_type: input.productType } : {}),
      ...(input.phoneModelId !== undefined ? { phone_model_id: input.phoneModelId } : {}),
      ...(input.caseStyleId !== undefined ? { case_style_id: input.caseStyleId } : {}),
      ...(input.baseType !== undefined ? { base_type: input.baseType } : {}),
      ...(input.code !== undefined ? { code: input.code } : {}),
    });
    return wireFullToTemplate(t);
  }
  /** Archive (delete) a template in the CLOUD — removes it for everyone (the owner /
   *  tenant). Server enforces ownership. Distinct from the per-device local hide. */
  async archiveTemplate(templateId: string): Promise<void> {
    return this.request("POST", `/api/templates/${templateId}/archive`);
  }
  /** Duplicate a template (cloud) → a fresh editable copy's id. */
  async duplicateTemplate(templateId: string): Promise<string> {
    const r = await this.request<{ template_id: string }>("POST", `/api/templates/${templateId}/duplicate`);
    return r.template_id;
  }
  /**
   * Open a fresh editable Draft on an existing template (cloud
   * `POST /api/templates/{id}/versions`). Returns the version's NUMBER + the
   * optimistic-concurrency token the editor must echo back on submit.
   */
  async createVersion(templateId: string): Promise<TemplateVersion> {
    const v = await this.request<WireVersionOut>("POST", `/api/templates/${templateId}/versions`);
    return wireVersionToView(v);
  }
  /** Data-driven phone catalog (cloud `GET /api/phone-models`) — Wizard Model select. */
  async listPhoneModels(): Promise<PhoneModelOption[]> {
    const rows = await this.request<{ id: string; brand: string; model: string; slug: string }[]>(
      "GET", "/api/phone-models");
    return (rows ?? []).map((p) => ({ id: p.id, brand: p.brand, model: p.model, slug: p.slug }));
  }
  /** Every catalog product type, hidden ones included (cloud `GET /api/product-types`,
   *  admin-only). A customer reads the active ones in `templateOptions()`. */
  async listProductTypes(): Promise<ProductTypeAdmin[]> {
    return (await this.request<ProductTypeAdmin[]>("GET", "/api/product-types")) ?? [];
  }
  /** Add a product type; the cloud derives its permanent code from the label. */
  async createProductType(input: { label: string; material?: string }): Promise<ProductTypeAdmin> {
    return this.request("POST", "/api/product-types", { label: input.label, material: input.material ?? "" });
  }
  /** Edit a product type. Absent fields are kept; the code never changes. */
  async updateProductType(
    id: string, input: { label?: string; material?: string; active?: boolean },
  ): Promise<ProductTypeAdmin> {
    return this.request("PATCH", `/api/product-types/${id}`, input);
  }
  /** Data-driven case-style catalog (cloud `GET /api/case-styles`) — Wizard Style select. */
  async listCaseStyles(): Promise<CaseStyleOption[]> {
    const rows = await this.request<{ id: string; code: string; label: string }[]>(
      "GET", "/api/case-styles");
    return (rows ?? []).map((c) => ({ id: c.id, code: c.code, label: c.label }));
  }
  /** Add a new phone model (cloud `POST /api/phone-models`, admin-only) — the Model
   *  select's inline "New" flow, mirroring `createCaseStyle`. Returns the created
   *  option so the caller can select it immediately. */
  async createPhoneModel(input: { brand: string; model: string; slug?: string }): Promise<PhoneModelOption> {
    const slug = input.slug
      ?? `${input.brand} ${input.model}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    const p = await this.request<{ id: string; brand: string; model: string; slug: string }>(
      "POST", "/api/phone-models", { brand: input.brand, model: input.model, slug });
    return { id: p.id, brand: p.brand, model: p.model, slug: p.slug };
  }
  /** Edit an existing phone model (cloud `PATCH /api/phone-models/{id}`, admin-only) —
   *  the Model select's inline "Edit" flow. Absent fields are kept. */
  async updatePhoneModel(
    id: string,
    input: { brand?: string; model?: string; structure?: Record<string, unknown> },
  ): Promise<PhoneModelOption> {
    const p = await this.request<{ id: string; brand: string; model: string; slug: string }>(
      "PATCH", `/api/phone-models/${id}`, input);
    return { id: p.id, brand: p.brand, model: p.model, slug: p.slug };
  }
  /** Add a new case style (cloud `POST /api/case-styles`, admin-only). Lets the operator
   *  create a style inline at template-creation time instead of leaving the list empty
   *  (spec 022 / #5). Returns the created option so the caller can select it immediately. */
  async createCaseStyle(input: { code: string; label?: string }): Promise<CaseStyleOption> {
    const c = await this.request<{ id: string; code: string; label: string }>(
      "POST", "/api/case-styles", { code: input.code, label: input.label ?? input.code });
    return { id: c.id, code: c.code, label: c.label };
  }
  /** Edit an existing case style (cloud `PATCH /api/case-styles/{id}`, admin-only) —
   *  the Style select's inline "Edit" flow. Absent fields are kept. */
  async updateCaseStyle(
    id: string,
    input: { code?: string; label?: string; materialAttrs?: Record<string, unknown> },
  ): Promise<CaseStyleOption> {
    const c = await this.request<{ id: string; code: string; label: string }>(
      "PATCH", `/api/case-styles/${id}`, {
        ...(input.code !== undefined ? { code: input.code } : {}),
        ...(input.label !== undefined ? { label: input.label } : {}),
        ...(input.materialAttrs !== undefined ? { material_attrs: input.materialAttrs } : {}),
      });
    return { id: c.id, code: c.code, label: c.label };
  }
  /** Admin image library (cloud `GET /api/image-assets?kind=…`) — the texture overlays +
   *  clear-case backgrounds the editor's product profile picks from. */
  async listImageAssets(kind: ImageAssetKind): Promise<ImageAsset[]> {
    const rows = await this.request<ImageAsset[]>(
      "GET", `/api/image-assets?kind=${encodeURIComponent(kind)}`);
    return rows ?? [];
  }
  /** Admin: presign an R2/CDN target for a new image-library asset (then PUT the bytes). */
  async presignImageAsset(kind: ImageAssetKind, ext: string): Promise<ReferenceUploadTarget> {
    return this.request("POST", "/api/image-assets/presign", { kind, ext });
  }
  /** Admin: persist an uploaded image-library asset (after the bytes are PUT to its target). */
  async createImageAsset(kind: ImageAssetKind, name: string, public_url: string): Promise<ImageAsset> {
    return this.request("POST", "/api/image-assets", { kind, name, public_url });
  }
  /** Admin: remove an image-library asset. */
  async deleteImageAsset(id: string): Promise<void> {
    await this.request("DELETE", `/api/image-assets/${id}`);
  }
  /** Patch the draft version's render_settings (e.g. the output format or Task-6 background).
   *  The cloud applies render_settings to the latest editable (non-locked) version payload
   *  by REPLACING the whole render_settings dict (unspecified fields fall back to the cloud
   *  schema defaults), so a caller editing an EXISTING template should send the full current
   *  render_settings merged with the changed field — not just the delta. Rotates the version
   *  token, so re-read it (createVersion reuses the draft with the fresh token) before submit. */
  async patchRenderSettings(
    templateId: string,
    renderSettings: {
      formats?: string[];
      output_size?: [number, number];
      fit_policy?: string;
      shadow_preset?: string | null;
      reflection_preset?: string | null;
      print_dpi?: number;
      /** Task 6: chosen Background option-group choice key. Omit to leave background absent
       *  (legacy templates that never set one); send `null` to CLEAR a previously-persisted
       *  background back to none (cloud RenderSettings.background: str | None). */
      background?: string | null;
      /** WS-BG: the RESOLVED sRGB `[r, g, b, a]` the chosen background paints. Send `null` to
       *  clear it. An older cloud DROPS this key (RenderSettings is not `extra="allow"`), which
       *  is why `docs/deploy-cloud.md` requires the cloud redeploy before the clients ship. */
      background_color?: [number, number, number, number] | null;
    },
  ): Promise<void> {
    await this.request("PATCH", `/api/templates/${templateId}`,
      { render_settings: renderSettings }, { raw: true });
  }

  // --- Editor approval (002) ------------------------------------------------
  /**
   * Persist corrected masks and move the version Draft → Pending. Routes by
   * version NUMBER (cloud system-of-record key), echoing the `versionToken` for
   * optimistic concurrency. `masks` is the canonical wire `MaskShape[]`.
   */
  async submitVersion(
    templateId: string,
    versionNumber: number,
    versionToken: string,
    masks: unknown,
    // Spec 017 US6: optional 4×[x,y] normalized brand-logo placement on the primary view.
    brandLogoPlacement?: [number, number][] | null,
    // P3: optional explicit `views` (e.g. the Wizard's chosen multi-view set). When
    // present the cloud persists exactly these views; when absent it applies `masks`
    // to the single primary view (the legacy single-view path).
    views?: { view_type: string; masks: unknown; layers?: unknown[] }[] | null,
  ): Promise<TemplateVersion> {
    const v = await this.request<WireVersionOut>(
      "POST",
      `/api/templates/${templateId}/versions/${versionNumber}/submit`,
      {
        version_token: versionToken,
        masks: normalizeMasksForWire(masks),
        brand_logo_placement: brandLogoPlacement ?? null,
        ...(views
          ? { views: views.map((v) => ({ ...v, masks: normalizeMasksForWire(v.masks) })) }
          : {}),
      },
    );
    return wireVersionToView(v);
  }
  async approveVersion(templateId: string, versionNumber: number): Promise<void> {
    return this.request("POST", `/api/templates/${templateId}/versions/${versionNumber}/approve`);
  }
  async rejectVersion(
    templateId: string,
    versionNumber: number,
    reason: string,
    // Opt-in terminal rejection (spec 018 / §4.1). Default bounces back to Draft.
    terminal = false,
  ): Promise<void> {
    return this.request(
      "POST",
      `/api/templates/${templateId}/versions/${versionNumber}/reject`,
      { reason, terminal },
    );
  }
  /** Admin: roll the template back to a prior approved version (spec 018 / §4.3). */
  async rollbackVersion(templateId: string, versionNumber: number): Promise<TemplateVersion> {
    const v = await this.request<WireVersionOut>(
      "POST", `/api/templates/${templateId}/versions/${versionNumber}/rollback`);
    return wireVersionToView(v);
  }
  /** Clone a Rejected/Archived version into a fresh editable Draft (spec 018 / §4.1). */
  async reopenVersion(templateId: string, versionNumber: number): Promise<TemplateVersion> {
    const v = await this.request<WireVersionOut>(
      "POST", `/api/templates/${templateId}/versions/${versionNumber}/reopen`);
    return wireVersionToView(v);
  }
  /** Read-only approval gate (any authenticated customer) — drives editor copy. */
  async approvalPolicy(): Promise<{ approvals_enabled: boolean }> {
    return this.request("GET", "/api/catalog/approval-policy");
  }
  /** Spec 019: persist a version's baked layered-mockup asset refs (hosted URLs +
   *  geometry — never pixels) so they sync across devices. The editor uploads the
   *  baked pixels to hosting first, then stores the resulting refs here. */
  async setBakedAssets(
    templateId: string,
    versionNumber: number,
    bakedAssets: Record<string, unknown>,
  ): Promise<TemplateVersion> {
    const v = await this.request<WireVersionOut>(
      "PUT",
      `/api/templates/${templateId}/versions/${versionNumber}/baked-assets`,
      { baked_assets: bakedAssets },
    );
    return wireVersionToView(v);
  }
  /** Super-admin / owning tenant-admin: set ownership & visibility metadata (spec 011 / A§3).
   * Marking a template `hidden`/`non_syncable` drops it from the Sync tab. */
  async setTemplateOwnership(
    templateId: string,
    input: TemplateOwnershipInput,
  ): Promise<TemplateOwnership> {
    return this.request("PATCH", `/api/admin/templates/${templateId}`, input);
  }
  /** Admin: presign an R2/CDN target to REPLACE a template's reference image (item 5).
   *  PUT the bytes to `upload_url`, then submit the returned `public_url` with the
   *  version payload — pixels never hit the API. */
  async templateReferenceUpload(templateId: string, ext: string): Promise<ReferenceUploadTarget> {
    return this.request("POST", `/api/templates/${templateId}/reference-upload`, { ext });
  }
  /** Review comment history (spec 018 / §5.2). Customers receive `public` comments only. */
  async listComments(templateId: string): Promise<ReviewComment[]> {
    const rows = await this.request<WireComment[]>(
      "GET", `/api/templates/${templateId}/comments`);
    return (rows ?? []).map(wireCommentToView);
  }
  /** Post a review comment. `internal` requires an admin role (server-enforced). */
  async addComment(templateId: string, input: AddCommentInput): Promise<ReviewComment> {
    const c = await this.request<WireComment>(
      "POST", `/api/templates/${templateId}/comments`,
      { comment_type: input.commentType, comment_text: input.commentText, version: input.version });
    return wireCommentToView(c);
  }

  // --- Storage connectors (spec 018 §0.6) -----------------------------------
  // These have NO caller right now, and that is deliberate: the "Upload destinations" UI
  // was removed in the 2026-08-05 sweep because no exporter ever read a connector and the
  // desktop transport does not speak SFTP (its own default provider) — see
  // docs/audit-history.md. The cloud routes are still live, so this client surface is kept
  // as the way back: re-add the UI on top of these once a transport can actually ship a
  // byte. Do not delete them as "dead code" without deleting the cloud side too.
  async listConnectors(): Promise<StorageConnector[]> {
    return (await this.request<StorageConnector[]>("GET", "/api/settings/connectors")) ?? [];
  }
  async createConnector(input: CreateConnectorInput): Promise<StorageConnector> {
    return this.request("POST", "/api/settings/connectors", input);
  }
  async updateConnector(id: string, input: UpdateConnectorInput): Promise<StorageConnector> {
    return this.request("PATCH", `/api/settings/connectors/${id}`, input);
  }
  async deleteConnector(id: string): Promise<void> {
    return this.request("DELETE", `/api/settings/connectors/${id}`);
  }
  async testConnector(id: string): Promise<ConnectorTestResult> {
    return this.request("POST", `/api/settings/connectors/${id}/test`);
  }
  /** Decrypted credential for the owner's OWN client transport (pixels never via cloud). */
  async getConnectorSecret(id: string): Promise<{ provider: string; config: Record<string, unknown>; secret: string }> {
    return this.request("GET", `/api/settings/connectors/${id}/secret`);
  }
  async addUploadLog(id: string, log: {
    file_name: string; status: string; error?: string; attempt?: number; bytes?: number;
  }): Promise<{ id: string }> {
    return this.request("POST", `/api/settings/connectors/${id}/logs`, log);
  }
  async listUploadLogs(id: string): Promise<UploadLogEntry[]> {
    return (await this.request<UploadLogEntry[]>("GET", `/api/settings/connectors/${id}/logs`)) ?? [];
  }

  // --- Template sync (009) --------------------------------------------------
  async syncRegistry(since?: string): Promise<unknown[]> {
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    return this.request("GET", `/api/templates/registry${qs}`);
  }

  // --- Telemetry (009 / 010) — metadata only -------------------------------
  async heartbeat(payload: HeartbeatPayload): Promise<void> {
    // The server (HeartbeatReq) is snake_case with a REQUIRED `device_id` and an
    // ISO-string `ts`; sending the camelCase payload raw 422s every tick.
    const wire = {
      device_id: payload.deviceId,
      online: payload.online,
      active_job_id: payload.activeJobId ?? null,
      progress: payload.progress ?? null,
      app_version: payload.appVersion ?? null,
      engine_version: payload.engineVersion ?? null,
      ts: payload.ts != null ? new Date(payload.ts).toISOString() : null,
    };
    // SYNTHESIS C-7: a heartbeat in flight when the document navigates (or the window
    // closes) is cancelled by the browser — net::ERR_ABORTED, 9× per AUD-6 sweep — and the
    // liveness sample is lost, which is precisely the state "Online desktops" reads.
    // The payload is a few hundred bytes, far under the 64KB keepalive limit.
    return this.request("POST", "/api/telemetry/heartbeat", wire, { keepalive: true });
  }
  async sendEvents(events: TelemetryEvent[]): Promise<{ accepted: number; rejected: number }> {
    // Server TelemetryEvent is { type, client_ts (ISO), data }. The ingest
    // (cloud/app/telemetry/ingest.py) reads snake_case keys and keys jobs on
    // data["id"]; forwarding the internal camelCase verbatim orphans every row
    // (jobs stuck "queued", Trace(0), unlinked trace/item rows) — so we map each
    // event's `data` to the exact wire shape here, exactly like `heartbeat` does.
    const iso = (ms: unknown): string | null =>
      typeof ms === "number" ? new Date(ms).toISOString() : null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapData = (type: string, d: any): unknown => {
      if (type === "job") return {
        id: d.jobId ?? d.id, device_id: d.deviceId, template_id: d.templateId,
        template_version_id: d.templateVersionId, engine_version: d.engineVersion,
        kind: d.kind, design_count: d.designCount,
        // The live JobRecord carries `views: ViewName[]`, not a scalar viewCount.
        view_count: d.viewCount ?? (Array.isArray(d.views) ? d.views.length : undefined),
        status: d.status, queued_at: iso(d.queuedAt), started_at: iso(d.startedAt),
        completed_at: iso(d.completedAt), duration_ms: d.durationMs, concurrency: d.concurrency,
      };
      if (type === "trace") return {
        job_id: d.jobId, item_id: d.itemKey ?? d.itemId, phase: d.phase,
        level: d.level ?? "info", start_ms: d.startedAt, end_ms: d.endedAt, message: d.message,
      };
      if (type === "item") return {
        job_id: d.jobId, design_slug: d.designSlug, view: d.view, status: d.status ?? "completed",
        // bootstrap emits native JobItemResult fields (durationMs/contentHash).
        error: d.error, timing_ms: d.timingMs ?? d.durationMs,
        output_content_hash: d.outputContentHash ?? d.contentHash,
      };
      if (type === "export") return {
        batch_id: d.batchId, job_id: d.jobId, design_slug: d.designSlug ?? d.designName,
        view: d.view, format: d.format,
        counts: d.counts ?? (typeof d.count === "number" ? { count: d.count } : undefined),
        status: d.status, local_ref: d.localRef ?? d.ref,
      };
      // `clog` already ships the camelCase keys the ingest reads — pass through.
      return d;
    };
    const wire = events.map((e) => ({
      type: e.type,
      client_ts: e.clientTs != null ? new Date(e.clientTs).toISOString() : null,
      data: mapData(e.type, e.data),
    }));
    return this.request("POST", "/api/telemetry/events", wire);
  }

  // --- Dashboard reads (010) — role-scoped ---------------------------------
  // The cloud serializers are snake_case with ISO timestamps; every method here
  // normalizes to the camelCase telemetry view-models (telemetry/types.ts) so
  // the Observability tab can render fields directly. The server `activity()`
  // returns ONLY {jobs, heartbeats} — exports live on a separate endpoint, so we
  // fetch and merge them here to keep the declared `exports` field honest.
  async dashboardActivity(query?: { tenant?: string; from?: string; to?: string }): Promise<{
    jobs: JobRecord[];
    heartbeats: HeartbeatPayload[];
    exports: ExportRecord[];
  }> {
    const qs = query ? `?${new URLSearchParams(query as Record<string, string>)}` : "";
    const exportQuery = query?.tenant ? { tenant: query.tenant } : undefined;
    const [activity, exports] = await Promise.all([
      this.request<WireActivity>("GET", `/api/dashboard/activity${qs}`),
      this.dashboardExports(exportQuery),
    ]);
    return {
      jobs: (activity?.jobs ?? []).map(wireJobToView),
      heartbeats: (activity?.heartbeats ?? []).map(wireHeartbeatToView),
      exports,
    };
  }
  /**
   * Unified, server-authoritative log feed (P2). `mode="activity"` returns every
   * row; `mode="errors"` only failures/errors. Merges real server-side generation
   * jobs + AI calls + exports with render jobs — so generation/AI-call activity and
   * errors (with their real error text) appear, not just client-forwarded renders.
   */
  async dashboardLog(opts?: { mode?: "activity" | "errors"; tenant?: string }): Promise<LogEntry[]> {
    const params: Record<string, string> = { mode: opts?.mode ?? "activity" };
    if (opts?.tenant) params.tenant = opts.tenant;
    const rows = await this.request<WireLogEntry[]>(
      "GET", `/api/dashboard/log?${new URLSearchParams(params)}`,
    );
    return (rows ?? []).map(wireLogToView);
  }
  async dashboardJob(jobId: string): Promise<{ job: JobRecord; items: unknown[]; traces: TraceEvent[] }> {
    const d = await this.request<WireJobDetail>("GET", `/api/dashboard/jobs/${jobId}`);
    return {
      job: wireJobToView(d.job),
      items: d.items ?? [],
      traces: (d.traces ?? []).map((t) => wireTraceToView(d.job.id, t)),
    };
  }
  async dashboardExports(query?: Record<string, string>): Promise<ExportRecord[]> {
    const qs = query ? `?${new URLSearchParams(query)}` : "";
    const rows = await this.request<WireExport[]>("GET", `/api/dashboard/exports${qs}`);
    return (rows ?? []).map(wireExportToView);
  }
  async dashboardStores(): Promise<unknown[]> {
    return this.request("GET", "/api/dashboard/stores");
  }
  /** KPI totals over the whole dataset. Throws (404) against a pre-WS-G cloud — the
   *  caller falls back to counting the rows it already has. */
  async dashboardSummary(tenant?: string): Promise<DashboardSummary> {
    const qs = tenant ? `?tenant=${encodeURIComponent(tenant)}` : "";
    return this.request("GET", `/api/dashboard/summary${qs}`);
  }

  // --- Client-log dashboard (spec 019 client observability, Task 11) ----------
  /**
   * Query the client-log event feed. Filters are forwarded as URLSearchParams;
   * undefined/null filters are omitted. Returns an array of `ClientLogRow` records
   * ordered by `occurred_at desc` (server-side). Requires admin auth.
   */
  async dashboardClientLogs(filters: {
    customer?: string;
    device?: string;
    level?: string;
    category?: string;
    q?: string;
    since?: string;
    until?: string;
    limit?: number;
    /** Exact match on context.requestId — every event of ONE request, client and cloud. */
    request_id?: string;
  }): Promise<ClientLogRow[]> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
    const q = qs.toString();
    const rows = await this.request<ClientLogRow[]>(
      "GET",
      `/api/dashboard/client-logs${q ? `?${q}` : ""}`,
    );
    return rows ?? [];
  }
}

export function createJsonClient(opts: JsonClientOptions): JsonClient {
  return new JsonClient(opts);
}
